import { reportBaseTranslations } from './reports/base';
import { cancelledServicesTranslations } from './reports/cancelled_services';
import { dailyDriverServiceTranslations } from './reports/daily_driver_service';
import { dailyServiceFrequencyTranslations } from './reports/daily_service_frequency';
import { driverAssignmentDetailTranslations } from './reports/driver_assignment_detail';
import { missedServicesTranslations } from './reports/missed_services';
import { serviceFrequencyTodayTranslations } from './reports/service_frequency_today';
import { serviceUsersTranslations } from './reports/service_users';
import type { th } from './th';

/** English locale — mirrors the Thai key structure exactly (type-checked). */
type Resources = { [N in keyof typeof th]: Record<keyof (typeof th)[N], string> };

export const en: Resources = {
  common: {
    mobileDevicesTitle: 'Mobile devices',
    mobileDevicesHint:
      'Reported by the app, including previously used devices. No logout reported does not mean the device is online.',
    mobileDevicesEmpty:
      'No device reports yet. Details appear after signing in with a supported app version.',
    mobileDevicesLoggedOut: 'Logged out',
    mobileDevicesNotLoggedOut: 'No logout reported',
    mobileDevicesOs: 'OS version',
    mobileDevicesApp: 'App version',
    mobileDevicesVersionBuild: '{{version}} (build {{build}})',
    mobileDevicesFirstSeen: 'First seen',
    mobileDevicesLastSeen: 'Last seen',
    mobileDevicesLogoutAt: 'Logged out at',
    mobileDevicesMore: 'Load more devices',
    appName: 'Cab Go',
    save: 'Save',
    saving: 'Saving…',
    cancel: 'Cancel',
    confirm: 'Confirm',
    create: 'Create',
    edit: 'Edit',
    delete: 'Delete',
    close: 'Close',
    menu: 'Menu',
    search: 'Search',
    loading: 'Loading…',
    noData: 'No data',
    vehiclePlateConflict: 'This plate is already registered for this provider and province',
    bookingAdvanceHorizonError:
      'The selected start exceeds this service’s advance booking window. Choose an earlier time.',
    bookingAdvanceLeadError:
      'The selected start is too soon. Choose a time that meets this service’s minimum advance notice.',
    bookingAdvanceLimitError:
      'The maximum number of active advance bookings for this service has been reached.',
    bookingAdvanceDisabledError:
      'This service does not accept advance bookings. Book now or choose another service.',
    bookingInstantDisabledError:
      'This service does not accept instant bookings. Schedule ahead or choose another service.',
    bookingEquipmentError: 'The selected equipment is unavailable. Review your selection.',
    automationDepartureRadiusError:
      'The departure radius must be greater than both the pickup and destination arrival radii; equal values are not allowed. Check the effective settings, including inherited values for providers and service areas.',
    bookingRouteShareError: 'This service does not allow route sharing. Change your selection.',
    bookingPassengerAreaError:
      'The pickup or destination is outside the passenger’s entitlement area. Change the locations or choose another service.',
    bookingServiceAreaError: 'The pickup or destination is outside the service area. Review the locations.',
    bookingRetryMismatch:
      'The original booking already exists with different details. Check that booking before starting a new one.',
    total: 'Total',
    items: 'items',
    status: 'Status',
    active: 'Active',
    suspended: 'Suspended',
    all: 'All',
    clearFilters: 'Clear filters',
    clear: 'Clear',
    inactiveShort: 'inactive',
    previousPeriod: 'Previous period',
    nextPeriod: 'Next period',
    // shared date-range filter (DateRangeFilter)
    dateRangeTitle: 'Select date range',
    rangeAll: 'All time',
    rangeToday: 'Today',
    rangeYesterday: 'Yesterday',
    rangeLast7: 'Last 7 days',
    rangeLast30: 'Last 30 days',
    rangeThisMonth: 'This month',
    rangeLastMonth: 'Last month',
    rangeThisYear: 'This year',
    rangeLastYear: 'Last year',
    rangeClear: 'Clear',
    rangeStart: 'Start date',
    rangeEnd: 'End date',
    datePickerTitle: 'Select date',
    dateTimePickerTitle: 'Select date and time',
    datePickerMonth: 'Month',
    datePickerYear: 'Year',
    datePickerPreviousMonth: 'Previous month',
    datePickerNextMonth: 'Next month',
    datePickerTime: 'Time',
    viewDetails: 'View details',
    copy: 'Copy',
    copyWebBuild: 'Copy web build information',
    webBuildCopied: 'Web build information copied',
    webBuildCopyFailed: 'Could not copy. Please record the displayed version.',
    back: 'Back',
    next: 'Next',
    yes: 'Yes',
    no: 'No',
    error: 'Something went wrong',
    forbidden: 'You do not have access to this page',
    legalTermsTitle: 'Terms of Service',
    legalPrivacyTitle: 'Privacy Policy',
    legalPlaceholder:
      'This document is being prepared. If you have any questions, please contact your provider.',
    retry: 'Retry',
    required: 'Required',
    optional: 'Optional',
    language: 'Language',
    actions: 'Actions',
    deleteConfirmTitle: 'Confirm deletion',
    deleteConfirmBody: 'This cannot be undone. Continue?',
    add: 'Add',
    upload: 'Upload',
    uploadTooLarge: 'File is too large',
    uploadWrongType: 'Unsupported file type',
    uploadFailed: 'Upload failed',
    refresh: 'Refresh',
    comingSoon: 'Coming soon',
    comingSoonBody: 'This feature is under development and will be available soon.',
    // image viewer modal
    imageViewerTitle: 'View image',
    imageViewerRotateLeft: 'Rotate left 90°',
    imageViewerRotateRight: 'Rotate right 90°',
    imageViewerZoomIn: 'Zoom in',
    imageViewerZoomOut: 'Zoom out',
    imageViewerReset: 'Reset',
    imageViewerOpenOriginal: 'Open original in new tab',
    webPushTitle: 'Notifications while the site is closed',
    webPushEnable: 'Enable',
    webPushDisable: 'Disable',
    webPushRegistering: 'Enabling…',
    webPushState_unsupported: 'This browser does not support Web Push',
    webPushState_unconfigured: 'Web Push has not been configured',
    webPushState_prompt: 'Off — enable to allow notifications',
    webPushState_registering: 'Registering this device…',
    webPushState_enabled: 'On — this device will receive notifications',
    webPushState_denied: 'Notifications are blocked; allow them in the site settings',
    webPushState_error: 'Could not enable Web Push; please try again',
  },
  nav: {
    mainNav: 'Main',
    dashboard: 'Dashboard',
    trips: 'Trips',
    care: 'Care',
    tracking: 'Live tracking',
    passengers: 'Passengers',
    drivers: 'Employees',
    caregivers: 'Caregivers',
    settings: 'Settings',
    dispatchSim: 'Offer lifecycle lab',
    logout: 'Log out',
  },
  auth: {
    loginTitle: 'Sign in',
    loginSubtitle: 'Operator console',
    email: 'Email',
    password: 'Password',
    login: 'Sign in',
    loggingIn: 'Signing in…',
    invalidCredentials: 'Invalid email or password',
    sessionExpired: 'Session expired — please sign in again',
  },
  // Public /account-deletion-request page — the URL the mobile apps' delete-
  // account row opens (App Store 5.1.1(v) / Play Data-safety web link).
  deletion: {
    title: 'Request account deletion',
    intro:
      'Verify your phone number with an OTP code to request deletion of your account and personal data from Cab Go.',
    actorType: 'Account type',
    actorPassenger: 'Customer (customer app)',
    actorAgent: 'Employee/Caregiver (agent app)',
    phone: 'Phone number',
    sendCode: 'Send OTP code',
    codeSent: 'A code was sent to your number',
    code: 'OTP code',
    note: 'Reason (optional)',
    submit: 'Submit deletion request',
    warning:
      'Once approved, personal data is permanently deleted and cannot be recovered. Trip and payment history is retained as required for accounting purposes.',
    successTitle: 'Request submitted',
    successBody: 'Our staff will review the request and delete your account as soon as possible.',
    completedTitle: 'Account deletion completed',
    completedBody: 'Your account deletion request has been completed.',
    rejectedTitle: 'Request not completed',
    rejectedBody: 'You may verify your phone number and submit a new deletion request.',
    submitAgain: 'Submit another request',
    codeInvalid: 'The code is invalid or has expired',
    rateLimited: 'Too many attempts — please wait a moment and try again',
  },
  settings: {
    amendment_applyControl: 'Emergency apply control',
    amendment_applyControlHelp:
      'Separate from accepting new requests. Stopping apply closes pending proposals and reserves received proposal money for refund; original service and confirmed history remain intact.',
    amendment_applyStopped: 'Applying booking changes is stopped',
    amendment_applyRunning: 'Applying booking changes is allowed',
    amendment_applySystemStop: 'The System stop remains effective even if this Provider resumes.',
    amendment_applyReason: 'Internal control-change reason',
    amendment_applyResume: 'Resume new proposals',
    amendment_applyStop: 'Stop applying booking changes',
    amendment_applyStopHelp:
      'Stop immediately prevents new applications. Pending proposals close through recovery, releasing their holds and reserving refunds. Already applied changes remain effective.',
    amendment_applyResumeHelp:
      'Resume allows newly submitted proposals. Requests created before the stop will still close and cannot be revived. Submission settings still apply.',
    amendment_title: 'Booking change policy',
    amendment_configure: 'Configure',
    amendment_conflict: 'Settings changed. Reload the saved baseline before retrying.',
    amendment_constraints:
      'Enter positive whole seconds. All timing fields must resolve before amendments can be enabled.',
    amendment_defaultOff: 'Default: disabled',
    amendment_denied: 'Administrator access is required.',
    amendment_enabled: 'Allow booking changes',
    amendment_help:
      'Provider overrides inherit unset values from System. Saved proposals retain their captured policy.',
    amendment_incomplete: 'Unavailable: timing configuration is incomplete.',
    amendment_inherit: 'Inherit System',
    amendment_localValue: 'Saved override: {{value}}',
    amendment_noProvider: 'Select a provider to configure its policy.',
    amendment_off: 'Disabled',
    amendment_on: 'Enabled',
    amendment_positiveInteger: 'Enter whole seconds from 1 to 2147483647.',
    amendment_reload: 'Reload saved settings, keep draft',
    amendment_reloaded: 'Latest baseline loaded. Your draft is preserved.',
    amendment_saved: 'Settings saved',
    amendment_scope: 'Configuration level',
    amendment_unresolved: 'No effective value configured',
    amendment_unset: 'Not configured',
    amendment_valueSource: 'Saved effective value: {{value}} seconds · {{source}}',
    amendment_SYSTEM: 'System',
    amendment_PROVIDER: 'Provider',
    amendment_AREA: 'Area',
    amendment_customerCutoffSeconds: 'Customer cutoff (seconds)',
    amendment_agentResponseSeconds: 'Driver response window (seconds)',
    amendment_replacementSearchSeconds: 'Replacement search window (seconds)',
    amendment_quoteGuaranteeSeconds: 'Quote guarantee window (seconds)',
    amendment_paymentSeconds: 'Payment window (seconds)',
    amendment_verificationSeconds: 'Proof review window (seconds)',
    amendment_finalizationSeconds: 'Finalization window (seconds)',
    amendment_holdSeconds: 'Schedule hold window (seconds)',
    amendment_overallSeconds: 'Overall request window (seconds)',

    partnerRefreshKeys: 'Refresh credentials',
    partnerInterruptedIssuance:
      'If issuance was interrupted, refresh this list and revoke any unwanted credential before issuing a replacement. An interrupted request may still complete on the server.',
    partnerConnections: 'Partner connections',
    partnerAdminOnly: 'Only provider administrators can manage partner connections.',
    partnerChooseProvider: 'Select a provider to continue.',
    partnerProviderContext: 'Provider: {{id}}',
    partnerServer: 'Current server environment: {{environment}}',
    partnerServerUnknown: 'Current server environment is unavailable.',
    partnerSandboxAvailable:
      'Test connections are available on this server. The server validates every request.',
    partnerSandboxUnavailable:
      'Test connection provisioning is unavailable. Testing is prohibited in production.',
    partnerCreate: 'Create connection',
    partnerRetry: 'Retry',
    partnerEmpty: 'No partner connections for this provider.',
    partnerIdentity: 'Partner identity',
    partnerNew: 'Create a new partner',
    partnerExisting: 'Link an existing partner ID',
    partnerId: 'Partner ID',
    partnerName: 'Partner name',
    partnerExplicitLink:
      'Use an existing ID only when you intend to link that partner. A matching name does not link identities. Services must be granted separately.',
    partnerSandboxCreate: 'Create a test connection',
    partnerUnavailable: 'This connection is unavailable for the selected provider.',
    partnerConnectionId: 'Connection ID',
    partnerEnvironment: 'Connection environment',
    partnerSandbox: 'Test connection',
    partnerKeys: 'API credentials',
    partnerManageKeys: 'Manage credentials',
    partnerServices: 'Service access',
    partnerManageServices: 'Manage service access',
    partnerKeysHelp:
      'Credentials apply only to this partner and provider connection. Revoking one immediately blocks API calls using that credential. Other credentials and connections remain unchanged.',
    partnerGrantHelp:
      'Disabling service access blocks new jobs. Existing jobs remain accessible for permitted reads, updates and cancellation while a credential is active; they are not cancelled.',
    partnerNoKeys: 'No credentials have been issued.',
    partnerRevoked: 'Revoked',
    partnerRevokeNamed: 'Revoke {{name}}',
    partnerRevokeConfirm: 'Confirm revocation',
    partnerRevokeWarning:
      'Revoke {{name}} ({{prefix}}, ID {{id}})? API calls using this credential will stop immediately.',
    partnerSecretOnce:
      'Copy this secret now and store it securely. It is shown only once and is removed when you close this view or switch providers.',
    partnerSecret: 'One-time secret',
    partnerServiceId: 'Service ID',
    partnerCopyServiceId: 'Copy Service ID',
    partnerServiceIdCopyFailed: 'Could not copy. Select and copy the Service ID manually.',
    partnerCopy: 'Copy secret',
    partnerCopied: 'Copied to clipboard.',
    partnerCopyFailed: 'Could not copy. Select and copy the secret manually.',
    partnerDismissSecret: 'I have saved the secret; hide it',
    partnerKeyName: 'Credential name',
    partnerSearchKeys: 'Search credentials…',
    partnerSearchServices: 'Search services…',
    partnerKeyPrefix: 'Key prefix',
    partnerCreatedAt: 'Created at',
    partnerIssueKey: 'Issue credential',
    partnerNewKey: 'New credential',
    partnerCreateKeyHelp:
      'Specify a name to identify this API credential. The secret will be shown once after creation.',
    partnerSecretOnceTitle: 'API Credential Secret',
    partnerPolicyHelp: 'Partner policies apply to new jobs. Existing jobs keep their captured policies.',
    partnerDeferredHelp:
      'Deferred payment permits service while payment remains outstanding. It does not mark the balance paid.',
    partnerDispatchHelp:
      'Admin review is the default. Full auto starts only the first search and its retries. After search exhaustion or a worker withdrawal before service, an administrator must start any eligible new search.',
    partnerNoServices: 'No services belong to this provider.',
    partnerPaymentPolicy: 'Partner payment policy',
    partnerPaymentPolicyShort: 'Payment Policy',
    partnerDispatchPolicy: 'Partner dispatch initiation',
    partnerDispatchPolicyShort: 'Dispatch Policy',
    partnerServiceHeader: 'Service',
    partnerKeyPrefixShort: 'Prefix',
    partnerPaymentPrepayShort: 'Prepay',
    partnerPaymentDeferredShort: 'Deferred',
    partnerDispatchAutoShort: 'Auto',
    partnerDispatchReviewShort: 'Review',
    partnerCustomPolicyTag: 'Custom',
    partnerDefaultPolicyTag: 'Default',
    partnerPolicyPREPAY_REQUIRED: 'Prepayment required',
    partnerPolicyDEFERRED: 'Deferred payment',
    partnerPolicyADMIN_REVIEW: 'Admin review',
    partnerPolicyFULL_AUTO: 'Full auto (first search)',
    partnerAccessEnabled: 'Access enabled',
    partnerAccessDisabled: 'Access disabled',
    partnerInherit: 'Use service default: {{policy}}',
    partnerEffective: 'Effective policy: {{policy}}',
    partnerSaveAccess: 'Save access policies',
    partnerEnableAccess: 'Enable service access',
    partnerDisableAccess: 'Disable service access',
    partnerInactiveService: 'Activate this service in service settings before enabling access.',
    partnerDisableWarning:
      'Disable new jobs for {{name}}? Existing jobs will remain accessible and will not be cancelled.',
    partnerConfirmDisable: 'Confirm disable',
    partnerConfigure: 'Configure policies',
    partnerSecretSavedConfirm: 'I have copied and securely stored this secret',
    partnerCustomPolicy: 'Custom',
    partnerDefaultPolicy: 'Default',
    partnerEvidenceTitle: 'Partner request evidence',
    partnerEvidenceRetention: 'Retention: {{days}} days. Each record shows its expiry.',
    partnerEvidenceEmpty: 'No request evidence is available for this connection.',
    partnerEvidenceLoadOlder: 'Load older requests',
    partnerEvidenceDetails: 'Request evidence details',
    partnerEvidenceView: 'View evidence',
    partnerEvidenceBackToResults: 'Back to results',
    partnerEvidenceReceivedAt: 'Received at (Bangkok time)',
    partnerEvidenceEndpoint: 'Endpoint',
    partnerEvidenceOutcome: 'Outcome',
    partnerEvidenceOutcome_PENDING: 'Pending',
    partnerEvidenceOutcome_INCOMPLETE: 'Incomplete — outcome not recorded',
    partnerEvidenceOutcome_CREATED: 'Created',
    partnerEvidenceOutcome_QUOTED: 'Quote returned',
    partnerEvidenceOutcome_REPLAYED: 'Idempotent replay',
    partnerEvidenceOutcome_REJECTED: 'Rejected',
    partnerEvidenceRequestId: 'Response request ID',
    partnerEvidenceEventId: 'Evidence attempt ID',
    partnerEvidenceProviderId: 'Provider ID',
    partnerEvidenceRelease: 'API release',
    partnerEvidenceHttpStatus: 'HTTP status',
    partnerEvidenceErrorCode: 'Error code',
    partnerEvidenceCompletedAt: 'Completed at (Bangkok time)',
    partnerEvidenceExpiresAt: 'Evidence expires at (Bangkok time)',
    partnerEvidencePresence: 'Submitted field presence',
    partnerEvidenceSubmitted: 'Submitted request',
    partnerEvidenceSubmittedBody: 'Submitted parsed JSON (redacted)',
    partnerEvidenceSubmittedIsInstant: 'Submitted isInstant',
    partnerEvidenceSubmittedStart: 'Submitted scheduledStart (original offset)',
    partnerEvidenceSubmittedEnd: 'Submitted scheduledEnd (original offset)',
    partnerEvidenceMissing: 'Missing',
    partnerEvidenceNull: 'null',
    partnerEvidenceInterpretation: 'Server interpretation',
    partnerEvidenceEvaluatedAt: 'Evaluated at (Bangkok time)',
    partnerEvidenceEffectiveInstant: 'Effective isInstant',
    partnerEvidenceTrue: 'true',
    partnerEvidenceFalse: 'false',
    partnerEvidenceEnabled: 'enabled',
    partnerEvidenceDisabled: 'disabled',
    partnerEvidenceSource_SUBMITTED: 'submitted',
    partnerEvidenceSource_DEFAULT: 'omitted; defaulted to true',
    partnerEvidenceResolvedStart: 'Server schedule start (UTC)',
    partnerEvidenceResolvedEnd: 'Server schedule end (UTC)',
    partnerEvidenceBookingRules: 'Booking availability rules',
    partnerEvidenceInstantRule: 'Instant booking: {{value}}',
    partnerEvidenceAdvanceRule: 'Advance booking: {{value}}',
    partnerEvidenceAdvanceLimits: 'Advance limits',
    partnerEvidenceAdvanceLimitsValue:
      '{{lead}} minute minimum lead · {{horizon}} day horizon · {{count}} job limit',
    partnerEvidenceStaffingDeadline: 'Staffing deadline (UTC)',
    partnerEvidenceRequestedQuoteId: 'Submitted quote ID',
    partnerEvidenceEffectiveQuoteId: 'Effective fare quote ID',
    partnerEvidenceQuoteResolution: 'Quote resolution',
    partnerEvidenceNone: 'None',
    partnerEvidenceUnavailable: 'Unavailable',
    partnerEvidencePending: 'Still pending',
    partnerEvidenceNoInterpretation: 'No server interpretation was recorded for this attempt.',
    partnerEvidenceCommitted: 'Committed result',
    partnerEvidenceCommitNotRecorded: 'No committed job result was recorded for this attempt.',
    partnerEvidenceJobId: 'Job ID',
    partnerEvidenceBookingId: 'Booking ID',
    partnerEvidenceTripRef: 'Trip reference',
    partnerEvidenceFareQuoteId: 'Committed fare quote ID',
    partnerEvidenceCommittedInstant: 'Committed isInstant',
    partnerEvidenceCommittedStart: 'Committed schedule start (UTC)',
    partnerEvidenceCommittedEnd: 'Committed schedule end (UTC)',
    partnerEvidenceInitialStatus: 'Initial job status',
    partnerEvidenceCreationChannel: 'Creation channel',
    partnerEvidenceReplayJobId: 'Original job ID for replay',
    partnerEvidenceQuoteSnapshot: 'Quote snapshot',
    partnerEvidenceQuoteCalculatedAt: 'Quote calculated at (Bangkok time)',
    partnerEvidenceQuoteValidUntil: 'Quote valid until (Bangkok time)',
    partnerEvidenceQuoteContractVersion: 'Quote contract version',
    partnerEvidenceQuotePricingRevision: 'Quote pricing revision',
    partnerEvidenceQuoteInstant: 'Quote isInstant',
    partnerEvidenceQuoteStart: 'Quote schedule start (UTC)',
    partnerEvidenceQuoteEnd: 'Quote schedule end (UTC)',
    partnerEvidenceQuoteMaterialMatch: 'Create request matches quote material',
    partnerEvidenceMatchYes: 'Yes',
    partnerEvidenceMatchNo: 'No',
    partnerEvidenceMatchUnknown: 'Unknown',
    partnerEvidenceCaptureBestEffort:
      'Capture is best-effort. Missing, expired, or unavailable evidence cannot be reconstructed; an empty list does not prove no request occurred.',
    partnerEvidenceResultJobId: 'Returned job ID',
    partnerEvidenceReplaySnapshotUnavailable:
      'This replay returned existing job {{jobId}}. Its original committed creation snapshot is unavailable here; the replay payload is not the original request.',
    partnerEvidenceAttempt: 'Request attempt',
    partnerEvidenceFailureStage: 'Failure stage',
    partnerEvidenceFailureStage_SCHEMA_VALIDATION: 'Schema validation',
    partnerEvidenceFailureStage_SERVICE_ACCESS: 'Service access',
    partnerEvidenceFailureStage_ROUTING: 'Routing',
    partnerEvidenceFailureStage_QUOTE_MATCH: 'Quote match',
    partnerEvidenceFailureStage_BOOKING_RULES: 'Booking rules',
    partnerEvidenceFailureStage_COMMIT: 'Database commit',
    partnerEvidenceFailureStage_POST_COMMIT: 'Post-commit processing',
    partnerEvidenceFailureStage_UNKNOWN: 'Unknown stage',
    partnerEvidenceRefresh: 'Refresh request evidence',
    partnerEvidenceComparison: 'Submitted → Interpreted → Recorded result',
    partnerEvidenceOpenWorkspace: 'Open request evidence',
    partnerEvidenceBackToConnections: 'Back to connections',
    partnerEvidenceSelectedOutsidePage: 'This selected attempt is outside the currently loaded results.',
    partnerEvidenceSelectAttempt: 'Select an attempt to compare submitted, interpreted, and recorded values.',
    partnerEvidenceNoMatches: 'No retained evidence matches these filters.',
    partnerEvidenceRange: 'Received time range',
    partnerEvidenceRange_all: 'All retained evidence',
    partnerEvidenceRange_today: 'Today (Bangkok time)',
    partnerEvidenceRange_24h: 'Last 24 hours',
    partnerEvidenceRange_dates: 'Calendar dates (Bangkok time)',
    partnerEvidenceRange_times: 'Exact date and time (Bangkok time)',
    partnerEvidenceDateFrom: 'Received from date (inclusive)',
    partnerEvidenceDateTo: 'Received through date (inclusive)',
    partnerEvidenceReceivedFrom: 'Received from (Bangkok time, inclusive)',
    partnerEvidenceReceivedBefore: 'Received before (Bangkok time, exclusive)',
    partnerEvidenceIdType: 'Identifier type',
    partnerEvidenceIdValue: 'Exact identifier',
    partnerEvidenceIdPlaceholder: 'Exact request ID or UUID — no passenger details',
    partnerEvidenceFilterAny: 'Any',
    partnerEvidenceId_request: 'Request ID',
    partnerEvidenceId_evidence: 'Evidence attempt ID',
    partnerEvidenceId_job: 'Job ID',
    partnerEvidenceId_quote: 'Quote ID',
    partnerEvidenceCopyRequestId: 'Copy request ID',
    partnerEvidenceCopyJobId: 'Copy job ID',
    partnerEvidenceApplyFilters: 'Apply filters',
    partnerEvidenceClearFilters: 'Clear filters',
    partnerEvidenceFilterError_invalidId:
      'Choose an identifier type and enter its exact safe ID. Job, quote, and evidence IDs must be UUIDs; request IDs use letters, digits, periods, underscores, or hyphens (up to 128 characters).',
    partnerEvidenceFilterError_invalidStatus: 'Enter an integer HTTP status from 100 to 599.',
    partnerEvidenceFilterError_invalidDates:
      'Enter valid calendar dates with the start no later than the end.',
    partnerEvidenceFilterError_invalidDateTime:
      'Enter valid Bangkok date and time bounds with the start earlier than the end.',
    partnerEvidenceFilterError_rangeRequired:
      'Enter at least one received-time bound for the selected range.',
    partnerEvidenceSubmitted_true: 'true',
    partnerEvidenceSubmitted_false: 'false',
    partnerEvidenceSubmitted_missing: 'Missing',
    partnerEvidenceSubmitted_null: 'null',
    partnerEvidenceSubmitted_invalid: 'Invalid submitted value',
    partnerEvidenceTimezoneNote:
      'Submitted timestamps retain their original offset; server interpretation and committed timestamps are shown in UTC.',
    partnerEvidenceFindRelated: 'Find related attempts',
    partnerEvidenceRelatedAttempts: 'Related retained attempts',
    partnerEvidenceRelatedEmpty:
      'No other retained match was found. Older, expired, or uncaptured attempts may not appear.',
    partnerEvidenceRelatedNoIds: 'No captured job or quote ID is available to search for related attempts.',

    routingSettingsTab: 'Settings',
    routingChecksTab: 'Route checks',
    routingSwitchesTab: 'Provider changes',
    routingExplicitSelection: 'Explicit provider selection',
    routingUnsaved: 'You have unsaved changes',
    routingSaved: 'Settings saved',
    routingTime: 'Date and time (Bangkok)',
    routingCaller: 'Usage',
    routingResult: 'Result',
    routingResponseTime: 'Response time',
    routingActor: 'Changed by',
    routingBefore: 'Before',
    routingAfter: 'After',
    routingSelection: 'Selection',
    routingRecord: 'Record ID',
    routingViewRecord: 'View details {{id}}',
    routingDetails: 'Record details',
    routingAll: 'All',
    routingDateRange: 'Date range (Bangkok)',
    routingClear: 'Clear filters',
    routingRetry: 'Retry',
    routingNoMatches: 'No records match your search',
    routingEmpty: 'No history yet',
    routingPage: 'Page {{page}} · {{count}} records (up to 25 per page)',
    routingChecksHelp:
      'Recorded route checks, newest first. Search retained history within the selected dates. These are not provider billing totals.',
    routingSwitchesHelp:
      'Changes made through System Settings, newest first. Provider filter matches either the previous or the new provider.',
    routingSearchChecks: 'Record ID, request ID, or error code',
    routingSearchSwitches: 'Actor name or record ID',
    routingProfile: 'Routing profile',
    routingDistance: 'Distance',
    routingEnvironment: 'Environment',
    routingError: 'Error code',
    routingRequestId: 'Request ID',
    routingRevision: 'Configuration revision',
    routingCopyId: 'Copy record ID',
    routingCopied: 'Copied',
    routingCopyFailed: 'Could not copy. Select the record ID and copy it manually.',
    routingStatusSUCCESS: 'Success',
    routingStatusNO_ROUTE: 'No route',
    routingStatusERROR: 'Error',
    routingStatusPENDING: 'Outcome unknown',
    routingCaller_map_route: 'Route check',
    routingCaller_dispatch: 'Dispatch',
    routingCaller_simulator: 'Simulation',
    routingCaller_seed: 'Sample data',

    systemSettingsHint: 'Platform defaults for all providers. Changes take effect only after you save.',
    systemSave: 'Save system settings',
    systemSaveScope: 'Saves all settings and app update policies on this tab.',
    systemNoUnsavedChanges: 'No unsaved system changes',
    systemDiscardChanges: 'Discard changes',
    systemBookingTiming: 'Booking timing',
    systemPartnerApi: 'Partner API',
    systemPartnerApiHint:
      'One limit for all Partner API endpoints. Each Partner–provider connection has its own quota, shared across its keys. Saved changes apply to subsequent requests without resetting current usage. Other API security limits still apply.',
    systemPartnerApiLimit: 'Requests per minute per connection',
    systemPartnerApiLimitHint: 'Whole number from {{min}} to {{max}}. Leave blank to use {{default}}.',
    systemPartnerApiLimitInvalid:
      'Enter a whole number from {{min}} to {{max}}, or leave blank for the default.',
    systemMobileFeaturesTab: 'Mobile features',
    systemMobileFeaturesHint:
      'Live Activities and registration replay save separately. Use the save action in each section.',
    systemAutomationTab: 'Trip automation',
    systemAutomationHint:
      'System defaults for automatic trip actions. Review and save changes in the configuration dialog.',
    systemMobileCopyProject: 'Copy {{name}} ID',
    systemMobileTechnicalDetails: 'Integration details',
    systemMobileFlow: 'Recorded flow',
    systemMobileProvider: 'Replay provider',
    systemMobileAgentProject: 'Agent project',
    systemMobileCustomerProject: 'Customer project',
    systemMobileRevision: 'Configuration revision',
    systemMobileNotConfigured: 'Not configured',
    systemMobileSaved: 'No unsaved replay changes',
    systemMobileConflict:
      'Replay settings changed since you opened them. Your draft is retained. Load the latest settings before editing again.',
    systemMobileReload: 'Discard replay changes and load latest settings',
    systemMobileUpdateEditHint:
      'Changing a version or download link turns off updates for that target until you verify and enable it again. Save with system settings below.',
    systemMobileUpdateOn: 'Updates enabled',
    systemMobileUpdateOff: 'Updates disabled',

    systemRouting: 'Routing provider',
    systemRoutingHint:
      'Applies to new route checks after saving. Existing job evidence stays unchanged. No automatic fallback.',
    systemRoutingEnvironment: 'Environment default',
    systemRoutingEffective: 'Active provider',
    systemRoutingAudit: 'Recent 100 route checks and configuration changes',
    systemTripLiveActivities: 'Trip Live Activities',
    systemTripLiveActivitiesHint:
      'Control trip status cards on iOS and Android separately for Customer and Agent. Changes apply only to this environment after saving.',
    systemTripLiveActivitiesDisableHint:
      'Turning off stops new cards and related reminders, and sends a request to close existing cards. Delivery to phones may be delayed by the network.',
    systemTripLiveActivitiesCustomer: 'Customer Live Activities',
    systemTripLiveActivitiesAgent: 'Agent Live Activities',
    systemTripLiveActivitiesSave: 'Save Live Activities',
    systemTripLiveActivitiesConflict:
      'These settings changed since you opened them. Your changes are retained. Load the latest settings before editing again.',
    systemTripLiveActivitiesReload: 'Discard changes and load latest settings',
    systemMobileObservability: 'Registration session replay',
    systemMobileObservabilityHint:
      'Record selected Agent registration sessions after phone verification to help diagnose problems. Recording stays off if the app cannot confirm the settings.',
    systemMobileReplayEnabled: 'Enable session replay',
    systemMobileSampleRate: 'Registration sessions to record (%)',
    systemMobileMinimumBuild: 'Minimum mobile build',
    systemMobilePlatforms: 'Enabled platforms',
    systemMobileFlavors: 'App variants (comma-separated)',
    systemMobileMasking: 'Masking mode',
    systemMobileMaskingDiagnosticSafe: 'Diagnostic safe (inputs and images remain masked)',
    systemMobileMaskingStrict: 'Strict masking',
    systemMobileUpdates: 'Mobile app updates',
    systemMobileUpdatesHint:
      'Set the latest version for an optional update reminder and the minimum version for a required update. Use three-part versions (for example, 1.4.9). Drivers can finish active work before a required update. Confirm availability separately for every app and platform before enabling.',
    systemMobileUpdateCustomer: 'Customer',
    systemMobileUpdateAgent: 'Agent',
    systemMobileUpdateMinimum: 'Minimum supported version',
    systemMobileUpdateLatest: 'Latest available version',
    systemMobileUpdateStoreUrl: 'Store or testing download link',
    systemMobileUpdateEnabled:
      'Enable updates — I verified the target version is downloadable from this link for this app and platform.',
    systemMobileSave: 'Save registration replay',

    diagnosticsOlderHistory: 'Older history',
    diagnosticsLatestHistory: 'Latest history',
    diagnosticsSummary:
      'Returned evidence in this time range and environment: {{mismatches}} mismatches / {{comparable}} comparable journeys; {{other}} non-comparable journeys; {{unlinked}} events without journey linkage.',
    diagnosticsTitle: 'Diagnostics',
    diagnosticsCapture: 'Capture controls',
    diagnosticsInvestigate: 'Investigate',
    diagnosticsState: 'Current capture policy',
    diagnosticsGlobal: 'Global gate',
    diagnosticsBaseline: 'Baseline',
    diagnosticsRevision: 'Saved revision {{saved}} · observed revision {{observed}}',
    diagnosticsStale: 'Observation is stale; capture fails OFF',
    diagnosticsOneInstance: 'Observed on this instance only',
    diagnosticsRetentionNotice:
      'New control history is retained for {{days}} days. Changes affect future records only, excluding business audits and Better Stack retention.',
    diagnosticsReason: 'Reason',
    diagnosticsTurnOff: 'Turn global capture OFF',
    diagnosticsTurnOn: 'Enable global capture gate',
    diagnosticsRetentionDays: 'Control history retention (1–3650 days)',
    diagnosticsSaveRetention: 'Apply to future records',
    diagnosticsUnresolved: 'Command outcome is unresolved. Read its receipt before issuing another command.',
    diagnosticsResolve: 'Read command receipt',
    diagnosticsNewCommand: 'Refresh state and allow a new command',
    diagnosticsTemporary: 'Temporary capture',
    diagnosticsScope: 'Target scope',
    diagnosticsTargetId: 'Target ID',
    diagnosticsProviderId: 'Provider ID',
    diagnosticsMinutes: 'Duration (1–60 minutes)',
    diagnosticsStart: 'Start temporary capture',
    diagnosticsStop: 'Stop',
    diagnosticsTarget: 'Target',
    diagnosticsGlobalGate: 'Global capture gate',
    diagnosticsCaptureAdded: 'Capture added',
    diagnosticsCaptureRemoved: 'Capture removed',
    diagnosticsAuditRetention:
      'This audit uses {{days}} days of retention from the policy before the change.',
    diagnosticsPreProviderNotice:
      'Provider capture starts after provider binding. To include passenger routes before provider selection, ask Agent P or a platform administrator to start an environment or exact-journey capture before reproducing. Missing earlier routes cannot be recovered retroactively.',
    diagnosticsEnabled: 'Enabled',
    diagnosticsDisabled: 'Disabled',
    diagnosticsMetadata: 'Metadata',
    diagnosticsEnableBaseline: 'Enable baseline capture',
    diagnosticsDisableBaseline: 'Disable baseline capture',
    diagnosticsHistoryHelp:
      'Search retained history by actor name, target ID, or audit ID within your access scope.',
    diagnosticsHistorySearch: 'Search history',
    diagnosticsHistorySearchHint: 'Actor name, target ID, or audit ID',
    diagnosticsHistoryAction: 'Change',
    diagnosticsAllActions: 'All changes',
    diagnosticsClearFilters: 'Clear filters',
    diagnosticsHistoryNoMatches: 'No history matches these filters.',
    diagnosticsHistoryEmpty: 'No retained control history yet.',
    diagnosticsHistoryTime: 'Date and time',
    diagnosticsHistoryActor: 'Actor',
    diagnosticsDetails: 'Details',
    diagnosticsViewDetails: 'View details',
    diagnosticsHistoryPage: 'Page {{page}} · {{count}} records',
    diagnosticsPreviousPage: 'Previous page',
    diagnosticsNextPage: 'Next page',
    diagnosticsChanges: 'What changed',
    diagnosticsSetting: 'Setting',
    diagnosticsBefore: 'Before',
    diagnosticsAfter: 'After',
    diagnosticsPolicyRevision: 'Revision',
    diagnosticsAuditId: 'Audit ID',
    diagnosticsResult: 'Result',
    diagnosticsNoEvidence: 'No evidence was returned for this search.',
    diagnosticsEvent: 'Event',
    diagnosticsEventId: 'Event ID',
    diagnosticsHistory: 'Control history',
    diagnosticsExpires: 'Expires',
    diagnosticsCleanup: 'Due records: {{due}} · last cleanup: {{last}} · failures: {{failures}}',
    diagnosticsEvidenceNotice:
      'Search up to 14 days. Missing or partial evidence cannot establish a matching fare. Client observations are untrusted evidence.',
    diagnosticsJourneyId: 'Journey ID (optional)',
    diagnosticsHours: 'Look back (hours, max 336)',
    diagnosticsAmounts: 'Quoted: {{quote}} THB · booked: {{booking}} THB',
    diagnosticsEvidence: 'Evidence details',
    diagnosticsPartial: 'More evidence exists. Narrow the time range or journey before drawing a conclusion.',
    diagnosticsReason_INCIDENT: 'Incident',
    diagnosticsReason_VERIFICATION: 'Verification',
    diagnosticsReason_ROUTINE: 'Routine',
    diagnosticsReason_COST_CONTROL: 'Cost control',
    diagnosticsScope_PROVIDER: 'Provider',
    diagnosticsScope_TRIP: 'Trip',
    diagnosticsScope_JOURNEY: 'Journey',
    diagnosticsScope_ENVIRONMENT: 'Whole environment',
    diagnosticsResult_MATCH: 'Matched',
    diagnosticsResult_MISMATCH: 'Mismatch',
    diagnosticsResult_INSUFFICIENT_EVIDENCE: 'Insufficient evidence',
    diagnosticsResult_NOT_APPLICABLE: 'Not applicable',
    diagnosticsAction_DIAGNOSTICS_GLOBAL_CHANGED: 'Global capture changed',
    diagnosticsAction_DIAGNOSTICS_BASELINE_CHANGED: 'Baseline changed',
    diagnosticsAction_DIAGNOSTICS_CAPTURE_STARTED: 'Temporary capture started',
    diagnosticsAction_DIAGNOSTICS_CAPTURE_STOPPED: 'Temporary capture stopped',
    diagnosticsAction_DIAGNOSTICS_RETENTION_CHANGED: 'Retention changed',

    title: 'Settings',
    menu: 'Menu',
    navGroupOrganization: 'Organization',
    navGroupService: 'Services',
    navGroupPolicy: 'Policies',
    navGroupReasons: 'Reasons & ratings',
    navGroupDeletedAccounts: 'Deleted accounts',
    permissions: 'Permissions',
    targetProvider: 'Target provider',
    capability: 'Capability',
    org: 'Organization',
    serviceBookingChannels: 'Booking channels',
    serviceNoBookingChannels: 'All channels disabled',
    serviceChannelWeb: 'Web (operator)',
    serviceChannelMobile: 'Customer mobile app',
    serviceChannelPartner: 'Partner API',
    servicePartners: 'Partners with service access',
    servicePartnerSettings: 'Partner bookings',
    servicePartnerNewJobsHelp: 'Applies to new jobs. Existing jobs keep their saved policies.',
    servicePartnerDefaults: 'Default Partner policies',
    servicePartnerDefaultsHelp: 'Used unless an individual Partner has an override.',
    servicePartnerDisabledHelp:
      'New Partner API bookings are off. Partner selections and policies are retained.',
    servicePartnerNoneSelected: 'No Partners selected. No Partner can create new jobs for this service.',
    servicePartnerNoConnections: 'No Partner connections available.',
    servicePartnerCustomize: 'Custom settings',
    servicePartnerCustomizeFor: 'Custom settings for {{name}}',
    servicePartnerInherited: 'Service default',
    servicePartnerOverride: 'Partner override',
    servicePartnerDeferredHelp: 'Service can start before payment. Outstanding charges remain due.',
    servicePartnerAutoHelp:
      'Starts the first search automatically. After exhaustion or withdrawal before service, staff must restart the search.',
    partnerServicesMoved: 'Manage booking channels and partner access on the service page.',
    serviceGroups: 'Service groups',
    vehicles: 'Vehicles',
    orgUsers: 'Org users',
    passengerGroups: 'Passenger groups',
    serviceAreas: 'Service areas',
    dispatchPolicy: 'Dispatch policy',
    driverDocumentPolicy: 'Agent document requirements',
    driverDocumentPolicyHelp:
      'New versions apply only to applications submitted after activation. Existing application snapshots do not change.',
    driverDocumentPolicyVersion: 'Active version {{version}}',
    driverDocumentPolicyConfirm:
      'Activate this document policy? Existing applications keep their submitted version.',
    driverDocumentPolicyHistory: 'Policy history',
    driverDocumentPolicyActivatedBy: 'Activated by {{actor}} · {{date}}',
    driverApplicationPolicy: 'Agent vehicle field requirements',
    driverApplicationPolicyHelp:
      'Controls which vehicle details DRIVE applicants must provide. New versions do not change existing applications.',
    driverApplicationPolicyVersion: 'Active version {{version}}',
    driverApplicationPolicyConfirm:
      'Activate this vehicle field policy? Existing applications keep their submitted version.',
    driverApplicationPolicyHistory: 'Policy history',
    driverApplicationPolicyHistoryRow: 'Version {{version}} · {{actor}} · {{date}}',
    vehicleField_make: 'Make',
    vehicleField_model: 'Model',
    vehicleField_color: 'Color',
    vehicleField_registrationYear: 'Registration year',
    vehicleFieldRequirement_REQUIRED: 'Required',
    vehicleFieldRequirement_OPTIONAL: 'Optional',
    driverDocumentType_DRIVER_LICENSE: "Driver's license",
    driverDocumentType_PUBLIC_DRIVER_LICENSE: "Public driver's license",
    driverDocumentType_COMPULSORY_MOTOR_INSURANCE: 'Compulsory motor insurance',
    driverDocumentType_VOLUNTARY_MOTOR_INSURANCE: 'Voluntary motor insurance',
    driverDocumentType_VEHICLE_PHOTO: 'Vehicle photo',
    driverDocumentRequirement_REQUIRED: 'Required',
    driverDocumentRequirement_OPTIONAL: 'Optional',
    driverDocumentRequirement_DISABLED: 'Disabled',
    equipment: 'Equipment',
    reasons: 'Reason codes',
    ratingReasons: 'Rating reasons',

    orgName: 'Organization name',
    orgContactDetails: 'Contact and address',
    orgAvailabilitySettings: 'Availability and location',
    orgCode: 'Org code',
    branch: 'Office/branch',
    hq: 'Head office',
    branchOffice: 'Branch',
    orgType: 'Org type',
    address: 'Address',
    country: 'Country',
    province: 'Province',
    district: 'District',
    subDistrict: 'Sub-district',
    postcode: 'Postcode',
    phone: 'Phone',
    email: 'Email',
    website: 'Website',
    workingHours: 'Working hours',
    bankAccount: 'Payment account',
    bankName: 'Bank',
    bankAccountName: 'Account name',
    bankAccountNo: 'Account number',
    availabilityBufferBeforeMin: 'Availability buffer before',
    availabilityBufferAfterMin: 'Availability buffer after',
    availabilityBufferValue: '{{count}} minutes',
    availabilityBufferHint:
      'Expands every non-terminal job by the configured time before and after it. Both values accept 0–1440 minutes.',
    driverLocationStaleAfter: 'Agent location timeout (seconds)',
    driverLocationStaleAfterValue: '{{count}} minutes',
    driverLocationStaleAfterEffectiveValue: 'Effective timeout: {{count}} minutes',
    driverLocationStaleAfterHint:
      'Treat an agent as unavailable for new jobs after no location update for this long (120–3600 seconds).',
    agentSelfRegistration: 'Employee app registration',
    agentSelfRegistrationEnabled: 'Open for employee registration',
    agentSelfRegistrationDisabled: 'Closed for employee registration',
    agentSelfRegistrationHint:
      'When disabled, this organization is hidden from Agent signup and direct registration requests are rejected.',
    agentOfferFare: 'Job service fare',
    agentOfferFareShown: 'Shown on Agent incoming offers',
    agentOfferFareHidden: 'Hidden on Agent incoming offers',
    agentOfferFareHint:
      'Show the job service fare on Agent incoming offers, not Agent earnings or payout. The fare may change based on actual service. Changes apply when offers are next fetched; already displayed offers may not update.',
    promptPay: 'PromptPay',
    promptPayEdit: 'Edit PromptPay',
    promptPayStatus: 'PromptPay status',
    promptPayEnabled: 'PromptPay enabled',
    promptPayDisabled: 'PromptPay disabled',
    promptPayAmountMode: 'Amount in QR',
    promptPayIncludeAmount: 'Include amount in QR',
    promptPayExcludeAmount: 'Do not include amount in QR',
    promptPayAmountModeHint: 'When omitted, the payer must enter the amount in Mobile Banking.',
    promptPayProxyType: 'Destination type',
    promptPayProxyPHONE: 'Phone number',
    promptPayProxyTAX_ID: 'Tax ID',
    promptPayProxyValue: 'PromptPay number',
    promptPayDisplayName: 'Recipient display name',
    promptPayUpdatedAt: 'Last updated',
    promptPayInvalidValue: 'Enter a valid PromptPay number',
    promptPayPreview: 'Preview: {{type}} {{value}}',
    promptPayRecipientWarning: 'CabGo does not verify the recipient name with a bank.',
    promptPayConfirmTitle: 'Confirm PromptPay change',
    promptPayConfirmBody: 'This change affects new QR codes immediately. Continue?',
    systemSettings: 'System settings',
    // Capability-matrix category headers (Settings → Permissions).
    permGroupDriverDocuments: 'Employee documents',
    permGroupPassengerRelations: 'Passenger relations',
    permGroupServiceGroups: 'Service groups',
    permGroupDispatchPolicy: 'Dispatch policy',
    permGroupAccessControl: 'Access control',
    agentLoginCodes: 'Agent login codes',
    loginCodesCapability: 'Manage agent login codes (ADMIN only)',
    loginCodesIntro:
      'Issue a temporary code when an agent cannot receive SMS. The agent enters it on the usual OTP screen until it expires.',
    loginCodesIdentity:
      'Verify the requester’s identity before sharing a code, and record a reason for every issuance.',
    loginCodesForbidden:
      'This feature requires an ADMIN account with permission to manage agent login codes.',
    loginCodesDisabled:
      'Agent login codes are disabled for this provider. Contact a system administrator to enable the capability in Permissions.',
    loginCodesKeyMissing:
      'Issuing and revealing codes is temporarily unavailable. Contact a system administrator.',
    loginCodesSelectProvider: 'Choose a provider to view and manage codes.',
    loginCodesNew: 'Issue a code',
    loginCodesPhone: 'Agent phone number',
    loginCodesPhoneHint:
      'Use the number the agent will enter in the app, for example 0812345678 or +66812345678.',
    loginCodesPhoneInvalid: 'Enter a valid phone number, for example 0812345678 or +66812345678.',
    loginCodesReason: 'Reason for issuance',
    loginCodesReasonRequired: 'Enter a reason.',
    loginCodesValidity: 'Validity (minutes)',
    loginCodesValidityHint: 'Defaults to 60 minutes. Choose 1–1,440 minutes, starting from issuance.',
    loginCodesValidityInvalid: 'Enter a whole number of minutes from 1 to 1,440.',
    loginCodesGenerate: 'Issue code',
    loginCodesGenerating: 'Issuing code…',
    loginCodesIssued: 'Code issued. Reveal it to view and share it with the agent.',
    loginCodesHistory: 'Current codes and history',
    loginCodesHistoryHint:
      'This list contains issuance details. Reveal an individual active code when needed.',
    loginCodesEmpty: 'No login codes have been issued for this provider.',
    loginCodesLoadFailed: 'Could not load login codes. Try again.',
    loginCodesActionFailed: 'The action could not be completed. Try again.',
    loginCodesAccessLost: 'Your access or the code’s status has changed. Reload the list.',
    loginCodesRefresh: 'Reload codes',
    loginCodesMore: 'Load more history',
    loginCodesIssuer: 'Issued by',
    loginCodesIssuedAt: 'Issued at',
    loginCodesExpiresAt: 'Expires at',
    loginCodesRevokedAt: 'Revoked at',
    loginCodesStatus: 'Code status',
    loginCodesStatus_ACTIVE: 'Active',
    loginCodesStatus_EXPIRED: 'Expired',
    loginCodesStatus_REVOKED: 'Revoked',
    loginCodesActions: 'Code actions',
    loginCodesReveal: 'Reveal code',
    loginCodesRevealing: 'Revealing code…',
    loginCodesRevealFor: 'Reveal code for {{phone}}',
    loginCodesCopy: 'Copy code',
    loginCodesCopyPhone: 'Copy phone number {{phone}}',
    loginCodesCopyFailed: 'Could not copy the login code. Select the code and copy it manually.',
    loginCodesPhoneCopyFailed: 'Could not copy the phone number. Select the number and copy it manually.',
    loginCodesHide: 'Hide code',
    loginCodesSecretTitle: 'Login code for {{phone}}',
    loginCodesReusable:
      'This code can be reused until expiry or revocation. Agents who have already signed in keep their normal sessions.',
    loginCodesRevoke: 'Revoke code',
    loginCodesRevokeFor: 'Revoke code for {{phone}}',
    loginCodesRevokeTitle: 'Revoke this login code?',
    loginCodesRevokeBody:
      'The code for {{phone}} will stop working immediately. Agents already signed in keep their sessions.',
    loginCodesRevoked: 'Code revoked.',
    loginCodesRegenerateTitle: 'Replace the active code?',
    loginCodesRegenerateBody:
      '{{phone}} already has an active code. Issuing a replacement immediately invalidates the previous code.',
    loginCodesRegenerate: 'Issue replacement',
    permGroupOther: 'Other capabilities',
    systemLegal: 'Legal documents',
    systemLegalHint:
      'Links opened from the terms and privacy pages in the web and mobile apps. Leave blank to show the default placeholder page.',
    systemTermsUrl: 'Terms of Service URL',
    systemPrivacyUrl: 'Privacy Policy URL',
    systemSupport: 'Support',
    systemSupportHint: 'Destination email of the mobile "Report a problem" button',
    systemSupportEmail: 'Support email',
    // Agent location cadence (issue #233) — seconds, blank = API default.
    systemAgentLocation: 'Agent location',
    systemAgentLocationHint:
      'How often the agent app reports its position — leave blank for the system defaults (60 s while online, 15 s during an active trip).',
    systemAgentLocationIdle: 'Online, awaiting jobs (seconds)',
    systemAgentLocationTrip: 'During an active trip (seconds)',
    systemOperatorStaffing: 'Operator scheduled-booking staffing',
    systemOperatorStaffingHint:
      'How long before pickup an operator-created scheduled booking must have an assigned agent (0–1,440 minutes).',
    systemOperatorStaffingLeadMinutes: 'Staffing lead time (minutes)',
    systemQuoteGuarantee: 'Fare quote guarantee',
    systemQuoteGuaranteeHint:
      'How long the quoted route and fare remain guaranteed for every provider (1–60 minutes).',
    systemQuoteGuaranteeMinutes: 'Quote guarantee (minutes)',
    systemCustomerServiceModeOrder: 'Customer service mode order',
    systemCustomerServiceModeOrderHint:
      'Sets the display order for service modes in new Customer App booking flows without changing service eligibility.',
    systemServiceModeRIDE_AND_CARE: 'Ride + care',
    systemServiceModeCARE: 'Care',
    systemServiceModeRIDE: 'Ride',
    systemServiceModeTRAVEL: 'Travel',
    systemServiceModeMoveUp: 'Move {{mode}} up',
    systemServiceModeMoveDown: 'Move {{mode}} down',
    systemServiceModeResetDefault: 'Reset to default order',
    // Account-deletion queue — SUPER_ADMIN, behind the public request form.
    deletionRequests: 'Deletion requests',
    deletionRequestsTotal: 'Total requests',
    deletionRequestedAt: 'Requested',
    deletionActor: 'Account type',
    deletionActorPassenger: 'Customer',
    deletionActorAgent: 'Agent',
    deletionAccount: 'Matched account',
    deletionNoAccount: 'No account found',
    deletionNote: "Requester's reason",
    deletionStatusPending: 'Pending',
    deletionStatusCompleted: 'Deleted',
    deletionStatusRejected: 'Rejected',
    deletionComplete: 'Delete account',
    deletionCompleteConfirmBody:
      'This permanently erases the personal data on this account and it can no longer sign in. Continue?',
    deletionReject: 'Reject request',
    deletionResolutionNote: 'Resolution note',
    deletionRejectNotePlaceholder: 'Reason for rejecting',
    deletionProcessedBy: 'Processed by',
    deletionRequestDetail: 'Request details',
    deletedAgents: 'Deleted agent accounts',
    deletedAgentsTotal: 'Archived accounts',
    deletedAgentSearchPlaceholder: 'Search internal ID or provider',
    deletedAgentDetail: 'Deleted agent archive',
    internalAgentId: 'Internal agent ID',
    deletedAt: 'Deleted at',
    formerSkills: 'Former skills',
    retainedTrips: 'Retained trips',
    completedTrips: 'Completed trips',
    retainedPayments: 'Payment records',
    retainedRatings: 'Ratings',
    retainedHistory: 'Retained history',
    tripReference: 'Trip reference',
    paymentSummary: '{{count}} payment record(s)',
    ratingSummary: '{{stars}} stars',
    deletedPassengers: 'Deleted passenger accounts',
    deletedPassengersTotal: 'Archived accounts',
    deletedPassengerSearchPlaceholder: 'Search user code',
    deletedPassengerDetail: 'Deleted passenger archive',
    internalPassengerId: 'User code',
    uploadLogo: 'Upload logo',
    removeLogo: 'Remove logo',
    serviceHealth: 'System health',
    svcDatabase: 'Database',
    svcCache: 'Cache (Redis)',
    svcStorage: 'File storage',
    healthOk: 'Healthy',
    healthDown: 'Down',
    healthDisabled: 'Disabled',

    fullName: 'Full name',
    shortName: 'Short name',
    category: 'Category',
    welfare: 'Welfare',
    commercial: 'Commercial',
    serviceConditions: 'Service conditions',
    providerSkills: 'Provider skills',
    skillDrive: 'Drive',
    skillCare: 'Care',
    eligibleGroups: 'Eligible passenger groups',
    bookingStyle: 'Booking style',
    instant: 'Instant',
    advance: 'Advance',
    usageLimits: 'Usage limits',
    perWeek: 'Per week',
    perMonth: 'Per month',
    unlimited: 'Unlimited',
    upToNTimes: 'Up to {{n}} times',
    advanceMinLead: 'Min lead time',
    advanceMaxHorizon: 'Max horizon',
    advanceMaxCount: 'Max advance bookings',
    minutes: 'min',
    days: 'days',
    times: 'times',
    hours: 'hr',
    allowRouteShare: 'Allow route sharing',
    bookingStyleShort: 'Booking',
    // service-group mode selector
    serviceGroupMode: 'Service mode',
    sgModeRideAndCare: 'Ride & care',
    sgModeCare: 'Care',
    sgModeRide: 'Ride',
    sgModeTravel: 'Travel',

    // pricing designer (FR-W-SET-PRICE-010)
    pricing: 'Pricing',
    pricingSetup: 'Pricing setup',
    pricingModelScope: 'The selected model applies to every package in this service.',
    pricingUpliftScope: 'Applies to prices and rates in every package.',
    pricingUpliftDetails: 'How the increase is applied',
    pricingPackages: 'Pricing packages',
    pricingPackageNumber: 'Package {{number}}',
    pricingEmptyHelp:
      'Add a package to configure pricing. Saving with no packages clears pricing for this service.',
    pricingInvalidHelp:
      'Review the highlighted values before saving. Check package details if a section is collapsed.',
    pricingBufferUnit: '{{name}} unit',
    pricingBufferValue: '{{name}} value',
    pricingRateType: 'Rate type',
    pricingTierNumber: 'Tier {{number}}',
    pricingTierContext: '{{axis}}, tier {{number}}',
    pricingTierHelp: 'Leave the final upper bound empty to cover the remaining distance or time.',
    pricingWindowNumber: 'Time window {{number}}',
    pricingWindowStart: 'Start time',
    pricingWindowEnd: 'End time',
    pricingSurchargeUnit: 'Surcharge unit',
    noPricing: 'No pricing configured',
    pricingModel: 'Pricing model',
    pricingUplift: 'Include a percentage increase in service prices',
    pricingUpliftPercent: 'Price increase',
    pricingUpliftHelp:
      'Applies to every package and monetary rate. Customers see the resulting prices. Whole-baht line amounts are balanced to the rounded increased total. Existing bookings keep their quoted terms.',
    pricingUpliftInvalid: 'Enter a percentage from 0 to 100, with up to two decimal places.',
    pricingFlat: 'Flat + overage',
    pricingMetered: 'Metered',
    pricingFlatDesc: 'A flat price per trip with an included distance/time, then charge the overage.',
    pricingMeteredDesc: 'Charge by actual distance and time in tiers.',
    pkgName: 'Package name',
    price: 'Price',
    perTrip: 'THB/trip',
    flatCoverage: 'Included coverage',
    maxDistance: 'Distance up to',
    maxDuration: 'Duration up to',
    overageRates: 'Overage rates',
    overageBuffer: 'Grace before charging overage',
    distanceOverageBuffer: 'Distance grace',
    timeOverageBuffer: 'Duration grace',
    highBufferWarning: 'This exceeds 100%; review it before saving',
    pOverageDistance: 'Distance',
    pOverageTime: 'Duration',
    bahtPerKm: 'THB/km',
    bahtPerHour: 'THB/hr',
    specialConditions: 'Special conditions',
    instantSurcharge: 'Instant booking',
    timeWindow: 'Special time window',
    addPricing: 'Add package',
    removePricing: 'Remove this package',
    addTier: 'Add tier',
    addTimeWindow: 'Add time window',
    removeRow: 'Remove',
    rest: 'Remainder',
    distanceKmAxis: 'Distance (km)',
    durationHoursAxis: 'Duration (hours)',
    baht: 'THB',
    percent: '%',
    tierFrom: 'From',
    tierTo: 'To',
    tierRate: 'Rate',
    nonNegativeValue: 'Value must be at least 0',
    rateFlat: 'Flat',
    ratePerUnit: 'Per unit',

    plate: 'License plate',
    plateProvince: 'Province',
    make: 'Make',
    model: 'Model',
    year: 'Year',
    registrationYear: 'Registration year',
    color: 'Color',
    vehicleType: 'Type',
    seats: 'Seats',
    callName: 'Call name',
    van: 'Van',
    pickup: 'Pickup',
    ambulance: 'Ambulance',
    sedan: 'Sedan',
    other: 'Other',

    firstName: 'First name',
    lastName: 'Last name',
    gender: 'Gender',
    employeeCode: 'Employee code',
    position: 'Position',
    department: 'Department',
    role: 'Role',
    roleOfficer: 'Center officer',
    roleManager: 'Manager',
    roleAdmin: 'Admin',
    roleOfficerDesc: 'Creates and dispatches jobs, tracks daily progress, manages customer data',
    roleManagerDesc: 'Full data access, approves employee/caregiver applications, runs reports',
    roleAdminDesc: 'Configures the entire system: services, service areas, and more',
    passwordNew: 'Password',
    passwordConfirm: 'Confirm password',

    groupName: 'Group name',
    // passenger-group areas + members (FR-W-SET-PAXGROUP-010)
    paxGroupAreas: 'Group service areas',
    paxGroupAllAreas: 'All areas',
    paxGroupMembers: 'Group members',
    paxGroupManageMembers: 'Manage members',
    paxGroupMemberCount: 'Members',
    paxGroupMembersCount: '{{n}} people',
    paxGroupAdd: 'Add',
    paxGroupRemove: 'Remove',
    paxGroupAddPlaceholder: 'Search passengers to add (name or phone)',
    paxGroupSearchMembers: 'Search group members',
    description: 'Description',
    pgCivilServant: 'Civil servant',
    pgGeneral: 'General public',
    pgChildYouth: 'Children & youth',
    pgMen: 'Men',
    pgUnderprivileged: 'Underprivileged',
    pgDisabled: 'Disabled',
    pgElderly: 'Elderly',
    pgWomen: 'Women',

    areaSize: 'Area size',
    dispatchConditions: 'Dispatch conditions',
    manageDispatchPolicy: 'Manage policy',
    dispatchPolicyManagedElsewhere:
      'Employee candidate-search and redispatch settings are configured through the versioned policy page. Legacy dispatch-only area values remain stored for rollback. Configure the active booking-eligibility out-of-boundary tolerance in the Service Area details.',
    driverSearchRadius: 'Employee search radius',
    redispatchRadius: 'Re-dispatch radius',
    outOfAreaRange: 'Out-of-boundary tolerance',
    outOfAreaRangeInvalid: 'Enter a whole number of 0 or more',
    serviceAreaBoundaryLegend: 'Service Area boundary',
    outOfAreaRangePreviewLegend: 'Approx. {{km}} km tolerance',
    outOfAreaRangePreviewDefaultLegend: 'Approx. {{km}} km tolerance (default)',
    outOfAreaRangeHint:
      'Travel checks both origin and destination; CARE checks the origin/meeting place only. Each required endpoint is measured to the nearest Service Area polygon edge. 0 means no out-of-polygon distance tolerance; exact-boundary behavior is unchanged. This is not the employee candidate-search radius in Dispatch Policy V2.',
    outOfAreaRangeNoGeometryHint:
      'This value has no effect until a boundary is defined; a Service Area without geometry is unconstrained.',
    redispatchInterval: 'Re-dispatch interval',
    redispatchAttempts: 'Re-dispatch attempts',
    dispatchStrategy: 'Dispatch strategy',
    dispatchStrategySequential: 'Sequential',
    dispatchStrategyBroadcast: 'Broadcast',
    dispatchFanout: 'Concurrent offers',
    km: 'km',
    sqKm: 'km²',
    seconds: 'sec',

    equipmentName: 'Equipment name',
    medical: 'Medical',
    general: 'General',

    reasonLabel: 'Reason',
    serviceMode: 'Service mode',
    modeTrip: 'Trip',
    modeCare: 'Care',
    event: 'Event',
    eventCancel: 'Cancel job',
    eventReject: 'Reject job',
    allowedAuthors: 'Allowed authors',
    authorProviderStaff: 'Provider staff',
    authorOrgUser: 'Org user',
    authorPassenger: 'Passenger',
    authorAgent: 'Agent',

    scoreThreshold: 'Score threshold',
    below3: 'Below 3 stars',
    below4: 'Below 4 stars',

    reasonsTotal: 'Total supporting reasons',
    scoreGiven: 'Score given',
    by: 'By',
    aboutReason: 'About this reason',
    details: 'Details',
    createEntity: 'Create {{name}}',
    editEntity: 'Edit {{name}}',
    createdDate: 'Created date',
    reasonStatus: 'Reason status',
    reasonName: 'Name',
    passengerFacingReason: 'Passenger-facing reason',
    reasonTranslationTh: 'Reason translation — Thai (optional)',
    reasonTranslationEn: 'Reason translation — English (optional)',
    passengerReasonTranslationTh: 'Passenger-facing translation — Thai (optional)',
    passengerReasonTranslationEn: 'Passenger-facing translation — English (optional)',
    viaMobileApp: 'Via mobile application',
    viaWebConsole: 'Via web console',
    createReasonTitle: 'Create reason',
    editReasonTitle: 'Edit reason',
    createRatingTitle: 'Create satisfaction rating reason',
    editRatingTitle: 'Edit satisfaction rating reason',
    createReasonAction: 'Create reason',

    areasTotal: 'Total service areas',
    aboutArea: 'About this service area',
    areaName: 'Area name',
    serviceGroupCount: 'Service groups',
    areaUnlinked: 'Not in use',
    areaUnlinkedHint:
      'No active service group references this boundary, so it does not affect bookings. Open a service group and select this area.',
    groupAreas: 'Service areas',
    groupAreasUnconstrained: 'Not set — serves everywhere',
    groupAreasHint:
      'Pick the areas this group covers. Travel checks both origin and destination; CARE checks the origin/meeting place only. Any required endpoint outside every boundary and beyond its out-of-area range cannot be booked. Select none to serve everywhere.',
    orgSites: 'Unit sites',
    siteType: 'Type',
    siteTypeParking: 'Parking area',
    siteTypeTerminal: 'Terminal',
    siteTypeOffice: 'Office',
    siteTypeDepot: 'Maintenance depot',
    siteTypeOther: 'Other',
    drawBoundary: 'Draw boundary',
    resetBoundary: 'Reset',
    removeBoundary: 'Remove boundary',
    finishBoundary: 'Finish',
    useProvinceBoundary: 'Use province boundary',
    provinceBoundaryFailed: 'Could not load the province boundary',
    provinceBoundarySource: 'Province boundaries: Royal Thai Survey Department via UN OCHA (CC BY-IGO 3.0)',
    deleteSite: 'Delete site',
    deleteWarningTitle: 'Delete warning',
    deleteSiteWarning: 'Deleting a unit site cannot be undone. Type {{phrase}} and confirm.',
    confirmDeleteAction: 'Confirm delete',
    createAreaTitle: 'Create service area',
    createAreaAction: 'Create area',
    editAreaTitle: 'Edit service area',
    selectProvince: 'Select province',
    region: 'Region',
    regionNorth: 'North',
    regionNortheast: 'Northeast',
    regionCentral: 'Central',
    regionEast: 'East',
    regionWest: 'West',
    regionSouth: 'South',

    usersTotal: 'Total org users',
    aboutEmployee: 'About this employee',
    personalInfo: 'Personal info',
    accountAccess: 'Account & access',
    createUserTitle: 'Create user',
    editUserTitle: 'User',
    genderMale: 'Male',
    genderFemale: 'Female',
    employeeStatus: 'Employee status',
    memberSince: 'Member since',
    changePassword: 'Change password',
    newPassword: 'New password',
    confirmNewPassword: 'Confirm new password',
    passwordMismatch: 'Passwords do not match',
    createOption: 'Create',
    showPassword: 'Show password',
    hidePassword: 'Hide password',

    // provider onboarding (PROVIDER_ONBOARDING_CREATE capability — issue #415)
    providerOnboarding: 'Create provider',
    onboardingSubtitle: 'Mint a brand-new provider together with its initial ADMIN account in one step.',
    onboardingProviderName: 'Provider name',
    onboardingCodeHint: 'The org code is generated by the system and shown after the operation succeeds.',
    onboardingAdminFirstName: 'Initial admin first name',
    onboardingAdminLastName: 'Initial admin last name',
    onboardingAdminEmail: 'Initial admin email',
    onboardingAdminPassword: 'Initial admin password',
    onboardingEmailInvalid: 'Invalid email address',
    onboardingPasswordTooShort: 'Password must be at least 8 characters',
    onboardingReceiptTitle: 'Provider created',
    onboardingReceiptAdmin: 'New provider initial admin',
    onboardingReceiptContext: 'You are still operating inside your current organization.',
    onboardingForbidden: 'You do not have access to this feature.',

    orgsTotal: 'Total organizations',
    aboutOrg: 'About this organization',
    hqOfficeLocation: 'Head office location',
    orgStatus: 'Organization status',
    orgTypeState: 'State enterprise',
    orgTypePrivate: 'Private organization',
    orgTypeGovernment: 'Government agency',
    orgTypeOther: 'Other',
    editOrgTitle: 'Organization info',
    createOrgTitle: 'New organization',
    roleSuperAdmin: 'Super admin',
  },
  trips: {
    avoidTollsNote: 'Routes prefer avoiding toll roads where possible. The price does not include tolls.',
    amendment_previous: 'Previous',
    amendment_unchanged: 'Unchanged',
    amendment_readOnly: 'Cannot be changed',
    amendment_locationUpdated: 'Location coordinates updated',
    amendment_currentComparison:
      'Current effective service compared with this request. This is not a historical before/after snapshot.',
    amendment_history: 'Request history',
    amendment_deliveryDetails: 'Notification delivery details',
    amendment_nextConsent: 'Waiting for the passenger to consent to finding another Agent.',
    amendment_nextAgent:
      'The Agent must review and accept these details. No operator action is required now.',
    amendment_nextPayment:
      'Additional payment or receipt verification is required. Review payment evidence below.',
    amendment_nextApply: 'The system is applying the accepted changes. Wait for confirmation.',
    amendment_searchGroup: 'Agent search · {{count}} loaded events',
    amendment_auditUnknown: 'Change request activity',
    amendment_error_AMENDMENT_MINIMUM_LEAD_TIME:
      'The selected start is too soon for this service’s minimum advance notice. Choose a later time and review again.',
    amendment_error_AMENDMENT_VEHICLE_CAPACITY_UNKNOWN:
      'The assigned vehicle has no seat capacity recorded. Update its actual capacity before requesting this change.',
    amendment_error_AMENDMENT_VEHICLE_CAPACITY_INSUFFICIENT:
      'The assigned vehicle has insufficient seats for this request. Review the passenger count and vehicle capacity.',
    amendment_error_AMENDMENT_VEHICLE_UNAVAILABLE:
      'The assigned vehicle is unavailable. Review the vehicle assignment and status.',
    amendment_error_AMENDMENT_AGENT_UNAVAILABLE:
      'The assigned Agent is unavailable for this change. Review the assignment.',
    amendment_error_AMENDMENT_AGENT_AREA_UNSUITABLE:
      'The assigned Agent does not serve the proposed pickup area. Review the pickup location and Agent service areas.',
    amendment_error_AMENDMENT_AGENT_SKILLS_UNSUITABLE:
      'The assigned Agent does not have the required service skills. Review the assignment.',
    amendment_error_AMENDMENT_VEHICLE_SHARING_UNSUPPORTED:
      'The assigned vehicle does not support the booking’s route-sharing requirements.',
    amendment_error_AMENDMENT_ROUTE_SHARE_IMMUTABLE:
      'Route-sharing consent cannot be changed through a change request.',
    amendment_event_AMENDMENT_SEARCH_STARTED: 'Replacement search started',
    amendment_event_AMENDMENT_SEARCH_COMPLETED: 'Search round completed',
    amendment_event_AMENDMENT_SEARCH_CANDIDATE_SKIPPED: 'Candidate skipped',
    amendment_event_AMENDMENT_OFFERED: 'Change offer sent',
    amendment_event_AMENDMENT_OFFER_TIMED_OUT: 'Change offer expired',
    amendment_event_AMENDMENT_OFFER_RESPONDED: 'Agent responded to change offer',
    amendment_event_AMENDMENT_REPLACEMENT_CONSENT: 'Replacement consent recorded or requested',
    amendment_event_AMENDMENT_APPLY_STARTED: 'Applying requested changes',
    amendment_event_AMENDMENT_APPLY_COMPLETED: 'Change application attempt completed',
    amendment_event_BOOKING_AMENDMENT_APPLIED: 'Booking changes applied',
    amendment_event_BOOKING_AMENDMENT_WITHDRAWN: 'Change request withdrawn',
    amendment_event_AMENDMENT_CLOSED: 'Change request closed',
    amendment_event_AMENDMENT_RECEIPT_CONFIRMED: 'Amendment receipt confirmed',
    amendment_event_AMENDMENT_REFUND_CONFIRMED: 'Amendment refund confirmed',
    amendment_event_AMENDMENT_REFUND_TRANSFER_CONFIRMED: 'Refund transfer confirmed',
    amendment_event_AMENDMENT_RECONCILIATION_REFUND_RESERVED: 'Funds reserved for refund',
    amendment_event_AMENDMENT_TRANSPORT_ACKNOWLEDGED: 'Notification delivery reviewed',
    amendment_event_AMENDMENT_PROOF_REVIEW: 'Payment proof awaiting review',
    amendment_event_AMENDMENT_PROOF_REJECTED: 'Payment proof rejected',

    amendment_followUp:
      'Agent responses and amendment payment/refund actions are not available in this panel yet. The original job continues while this request is pending.',
    amendment_title: 'Booking changes',
    amendment_request: 'Request booking changes',
    amendment_preview: 'Review price and changes',
    amendment_submit: 'Submit proposal',
    amendment_close: 'Close',
    amendment_summaryFinancePending: 'Financial follow-up pending',
    amendment_summaryPayment: 'Awaiting additional payment or proof review',
    amendment_summaryUnavailable: 'Changes are unavailable for this job’s current status or conditions',
    amendment_summaryService: 'Service type',
    amendment_summarySchedule: 'Booking timing',
    amendment_showUnchanged: 'Show unchanged details',
    amendment_hideUnchanged: 'Hide unchanged details',
    amendment_field: 'Field',
    amendment_requestedAt: 'Requested at',
    amendment_respondBy: 'Respond by',
    amendment_changedDetails: 'Requested changes',
    amendment_calculationDetails: 'Calculation details',
    amendment_copyRecord: 'Copy record reference',
    amendment_noDifferences: 'No differences in the compared details',
    amendment_summaryRequest: 'Current request',
    amendment_summaryNoActive: 'No active request',
    amendment_summaryCanRequest: 'You can request changes to this booking',
    amendment_summaryNoHistory: 'No closed requests yet',
    amendment_summaryDetails: 'View details and history',
    amendment_originalRemains: 'The confirmed service remains in effect until this proposal is applied.',
    amendment_confirmed: 'Confirmed service',
    amendment_proposed: 'Proposed changes',
    amendment_scheduledStart: 'Service start',
    amendment_scheduledEnd: 'Service end',
    amendment_pickupApptAt: 'Pickup appointment',
    amendment_arriveByAt: 'Arrive by',
    amendment_stationary: 'Waiting allowance (minutes)',
    amendment_notSet: 'Not set',
    amendment_unavailable: 'Unavailable',
    amendment_share: 'Allow route sharing',
    amendment_yes: 'Yes',
    amendment_no: 'No',
    amendment_link: 'Continuation',
    amendment_linked: 'Continue with the original job',
    amendment_independent: 'Independent job',
    amendment_fare: 'Service price (THB)',
    amendment_financeUnavailable:
      'Current receipt and refund totals are not available here. Check the payment records; this does not mean the balance is zero.',
    amendment_money: 'Payment impact (THB)',
    amendment_waiver: 'Applied waiver (not cash)',
    amendment_available: 'Available confirmed funds',
    amendment_topup: 'Additional payment required',
    amendment_refund: 'Additional refund obligation',
    amendmentFinance_refundTransfer: 'Record one combined refund transfer',
    amendmentFinance_selectedRefunds: '{{count}} selected refund obligations',
    amendmentFinance_selectRefundItem: 'Include this refund',
    amendmentFinance_selectRefund: 'Include refund {{id}}',
    amendmentProof_title: 'Amendment payment evidence',
    amendmentProof_RESERVED: 'Upload in progress',
    amendmentProof_PENDING: 'Awaiting Admin review — no receipt yet',
    amendmentProof_REJECTED: 'Evidence rejected — resubmission allowed',
    amendmentProof_CONFIRMED: 'Receipt confirmed by Admin',
    amendmentProof_UPLOAD_FAILED: 'Upload failed — submit a new image request',
    amendmentProof_notCash:
      'An uploaded slip is evidence only. Admin confirmation records money received, even if the amendment has already closed.',
    amendmentProof_confirm: 'Confirm receipt from this proof',
    amendmentProof_reject: 'Reject this proof',
    amendmentProof_rejectionReason: 'Reason shown to the customer',
    amendmentProof_cashConsent: 'I checked this proof and confirm the stated amount was received.',
    amendmentProof_submitted: 'Submitted at',
    amendmentProof_view: 'View payment evidence',
    amendmentProof_upload: 'Submit payment evidence',
    amendmentProof_image: 'Slip image',
    amendmentProof_uploadRetry:
      'Retry sends the same image request. If the upload is marked failed, close and start a new submission. Check the list before submitting again.',
    amendmentProof_pendingCount: '{{count}} proofs awaiting review across this trip',
    amendmentProof_uploadRevision: 'Upload proof for revision {{revision}}',
    amendmentProof_empty: 'No amendment payment evidence submitted.',
    amendmentFinance_title: 'Amendment payments and refunds',
    amendmentFinance_receive: 'Confirm received money',
    amendmentFinance_refund: 'Confirm completed refund',
    amendmentFinance_reserve: 'Reserve money for refund',
    amendmentFinance_confirm: 'Confirm this record',
    amendmentFinance_retry: 'Retry the same record',
    amendmentFinance_confirmHelp:
      'Record money actually received or refunded. A submitted slip alone is not a cash confirmation.',
    amendmentFinance_reserveHelp: 'Reserve this amount for refund. This does not record a completed refund.',
    amendmentFinance_amount: 'Amount (whole baht)',
    amendmentFinance_reference: 'Transaction reference',
    amendmentFinance_proof: 'Checked evidence reference',
    amendmentFinance_cashConsent: 'I checked the evidence and confirm the money was received or refunded.',
    amendmentFinance_reserveConsent: 'I confirm this amount is to be reserved for refund.',
    amendmentFinance_retryHelp:
      'The result is uncertain or was rejected. Retry sends the exact same record without creating a new request. Before starting another record, refresh and check whether this one was recorded.',
    amendmentFinance_maximum: 'Maximum available amount',
    amendmentFinance_target: 'Related record',
    amendmentFinance_asOf: 'Data as of {{time}}',
    amendmentFinance_received: 'Recorded money received',
    amendmentFinance_refunded: 'Completed refunds',
    amendmentFinance_reserved: 'Reserved, awaiting refund',
    amendmentFinance_uninitialized:
      'Amendment accounting has not been initialized. These totals are unavailable; check the existing payment record.',
    amendmentFinance_receiveRevision: 'Record receipt for revision {{revision}}',
    amendmentFinance_receipts: 'Recorded receipts',
    amendmentFinance_noReceipts: 'No receipts in this amendment account.',
    amendmentFinance_adminConfirmed: 'Confirmed by an administrator',
    amendmentFinance_legacy: 'Existing payment record',
    amendmentFinance_record: 'Record reference',
    amendmentFinance_proofRecorded: 'Evidence reference recorded',
    amendmentFinance_noProof: 'No evidence reference recorded',
    amendmentFinance_reconciliation: 'Money awaiting reconciliation',
    amendmentFinance_noReconciliation: 'No reconciliation allocations.',
    amendmentFinance_availableRefund: 'Available to reserve for refund',
    amendmentFinance_refunds: 'Refund obligations',
    amendmentFinance_noRefunds: 'No refund obligations in this account.',
    amendment_moneyHelp:
      'Reserved refunds cannot fund this proposal. A slip is pending evidence; Admin confirmation records payment or refund completion.',
    amendment_detach: 'Propose converting the return leg to an independent job',
    amendment_reason: 'Reason',
    amendment_withdrawRetryHelp:
      'Your reason is retained. Check the latest request status before retrying withdrawal.',
    amendment_retryHelp:
      'Your draft is retained. Review updated information and request a new preview if terms have changed.',
    amendment_freshPrice: 'A fresh price requires your consent.',
    amendment_originalPrice: 'Calculated using the evidenced agreed pricing terms.',
    amendment_expires: 'Price valid until {{time}}',
    amendment_expiredPreview: 'This preview has expired. Request a new preview.',
    amendment_freshConsent: 'I agree to this fresh price and the displayed payment impact',
    amendment_detachConsent:
      'I agree to independent return-job proposals; the original continuation stays effective until application succeeds',
    amendment_revision: 'Proposal {{revision}}',
    amendment_deadline: 'Current step deadline: {{time}}',
    amendment_review: 'Review details',
    amendment_withdraw: 'Withdraw proposal',
    amendment_confirmWithdraw: 'Confirm withdrawal',
    amendment_reload: 'Retry loading',
    amendment_empty: 'No change proposals yet',
    amendment_more: 'Load earlier proposals',
    amendment_state_PENDING_AGENT: 'Waiting for Agent acceptance',
    amendment_state_PENDING_PAYMENT: 'Waiting for additional payment confirmation',
    amendment_state_READY_TO_COMMIT: 'Accepted; awaiting application',
    amendment_state_APPLIED: 'Applied',
    amendment_state_EXPIRED: 'Expired',
    amendment_state_WITHDRAWN: 'Withdrawn',
    amendment_state_REJECTED: 'Rejected',
    amendment_state_SUPERSEDED: 'Change request closed',
    amendment_timing_CONTINUATION_SERVICE_BOUNDARY_REQUIRED:
      'The original continuation has no confirmed service boundary. A scheduled independent-job proposal is required.',
    amendment_timing_CUTOFF_REACHED: 'The request cutoff has passed.',
    amendment_timing_INSUFFICIENT_QUOTE_VALIDITY:
      'The route/price validity is too short to complete the required steps. Request another preview.',
    automation_waiveAmount: 'Amount to waive',
    automation_afterReview: 'Job balance after this review',
    automation_configure: 'Configure',
    automation_save: 'Save settings',
    automation_discardHelp: 'You have unsaved changes. Discard them?',
    automation_discard: 'Discard changes',
    automation_keepEditing: 'Keep editing',
    automation_arrivalGroup: 'Arrival at pickup and destination',
    automation_pickupGroup: 'Automatic pickup',
    automation_completionGroup: 'Automatic completion',
    automation_sharedGroup: 'Shared departure and countdown conditions',
    automation_default: 'Use system default',
    automation_inheritSystem: 'Inherit from system',
    automation_inheritProvider: 'Inherit from provider',
    automation_savedValue: 'Saved effective value: {{value}} · {{source}}',
    automation_range: 'Enter a whole number between {{min}} and {{max}}',
    automation_radiusRange: 'Allowed range: {{min}}–{{max}} m (whole numbers)',
    automation_departureHelp:
      'The departure radius must be greater than both arrival radii, including inherited values; equal values are not allowed. For example, if both arrival radii are {{arrival}} m, departure must be at least {{departure}} m.',
    automation_policyTiming:
      'Changes apply after saving to newly started jobs. Started jobs retain their saved policy. Empty fields inherit; the resolved values are confirmed after saving.',
    automation_pickupHelp:
      'Eligible departure notifies the driver and starts a countdown. Its start anchors billable distance and time. The driver can cancel with a reason.',
    automation_completionHelp:
      'Completion also means passenger drop-off. Eligible departure notifies the driver and starts a countdown; billing ends at the countdown start.',
    automation_sharedHelp:
      'Pickup and completion share these values. Automatic actions require eligible GPS evidence and confirmed notification delivery.',
    automation_savedRule:
      'Saved condition: beyond {{radius}} m for {{dwell}} seconds → {{countdown}} countdown (when enabled and all other conditions are met)',
    automation_countdownSeconds: '{{count}} seconds',
    automation_countdownOneMinute: '1 minute',
    automation_countdownMinutes: '{{count}} minutes',
    automation_countdownOneMinuteSeconds: '1 minute {{seconds}} seconds',
    automation_countdownMinutesSeconds: '{{minutes}} minutes {{seconds}} seconds',
    automation_warningExample:
      'Example: plan 20 km + grace 4 km + warning at 20% of plan (4 km) = alert at 28 km. The warning threshold does not add free distance.',
    automation_loading: 'Loading trip automation…',
    automation_meters: '{{value}} m',
    automation_km: 'km',
    automation_minutes: 'min',
    automation_unknown: 'Not evaluated',
    automation_counting: 'Awaiting driver response',
    automation_remaining: '{{seconds}} seconds remaining',
    automation_elapsedSeconds: '{{seconds}} seconds from {{baseline}}',
    automation_waitServer: 'Deadline reached; awaiting server confirmation',
    automation_anchor: 'Countdown started',
    automation_deadline: 'Response deadline',
    automation_stale: 'Data may be stale. Refresh before taking action.',
    automation_activeAttempts: 'In progress',
    automation_evidenceHelp:
      'GPS indicates arrival, not boarding or alighting. Billing anchors can differ from the time a status change is committed.',
    automation_noEvidence:
      'No qualifying arrival evidence is available. This does not prove the location was never visited.',
    automation_OUTBOUND: 'Outbound',
    automation_RETURN: 'Return',
    automation_qualifiedAt: 'Arrival qualified',
    automation_entryAt: 'Entered area',
    automation_receivedAt: 'Evidence received',
    automation_gpsQuality: 'Distance to endpoint {{distance}} m · GPS accuracy {{accuracy}} m',
    automation_viewEvidence: 'View map evidence',
    automation_job: 'Job',
    automation_outstanding: 'Job outstanding balance',
    automation_waiveImpact:
      'Waive all outstanding overage without refunding received money or recording a receipt. Other outstanding debt can still block the next booking.',
    automation_approveImpact:
      'Approve the overage for normal collection. Approval does not mean payment has been received.',
    automation_confirmReview: 'Confirm review',
    automation_closureHelp:
      'Choose the effective service end from the evidence, then preview the calculation. The actual administrative action time is recorded separately.',
    automation_serviceTotal: 'Total service fare',
    automation_billableMetrics: 'Calculated distance and duration',
    automation_closureReviewPending:
      'Overage review is still required after closure before booking or starting the next job.',
    automation_closureNoReview:
      'This preview requires no further overage review. Outstanding debt still follows normal payment rules.',
    automation_gpsPickup: 'GPS-observed pickup arrival',
    automation_gpsDropoff: 'GPS-observed destination arrival',

    automation_title: 'Trip automation',
    automation_help:
      'Record arrival evidence after service starts. Automatic transitions default to off. Configure arrival radii for pickup and destination separately; the shared departure radius must be greater than both.',
    automation_effective: 'Effective: {{value}} ({{source}})',
    automation_inherit: 'Inherit',
    automation_off: 'Off',
    automation_on: 'On',
    automation_SYSTEM: 'System',
    automation_PROVIDER: 'Provider',
    automation_SERVICE_AREA: 'Service area',
    automation_pickupArrivalEnabled: 'Automatically record arrival at pickup',
    automation_dropoffArrivalEnabled: 'Automatically record arrival at destination',
    automation_pickupEnabled: 'Automatic passenger pickup countdown',
    automation_completionEnabled: 'Automatic completion countdown',
    automation_pickupRadiusM: 'Pickup arrival radius (m)',
    automation_dropoffRadiusM: 'Destination arrival radius (m)',
    automation_arrivalDwellSec: 'Arrival dwell (seconds)',
    automation_departureRadiusM: 'Departure radius (m)',
    automation_departureDwellSec: 'Departure dwell (seconds)',
    automation_countdownSec: 'Response countdown (seconds)',
    automation_warningPercent: 'Additional distance warning (%)',
    automation_warningHelp: 'Effective {{value}}%; added to plan plus saved pricing grace',
    automation_review: 'Overage review',
    automation_reviewNotRequired: 'Overage review not required because there are no additional charges',
    automation_PENDING: 'Awaiting review',
    automation_APPROVED: 'Approved',
    automation_WAIVED: 'Waived',
    automation_reason: 'Reason',
    automation_approve: 'Approve overage',
    automation_waive: 'Waive all outstanding overage',
    automation_closure: 'Close a started service',
    automation_effectiveAt: 'Effective service end (Bangkok time)',
    automation_preview: 'Preview closure',
    automation_confirmClosure: 'Confirm closure',
    automation_distanceWarning: 'Distance review threshold',
    automation_unevaluable: 'Insufficient evidence to evaluate',
    automation_exceeded: 'Threshold reached',
    automation_withinThreshold: 'Below threshold',
    automation_planGrace: 'Plan + pricing allowance',
    automation_observedThreshold: 'Observed / warning threshold',
    automation_candidate: 'Calculated outstanding',
    automation_waived: 'Waived outstanding',
    automation_noReceipt: 'Waiver does not record a payment receipt.',
    automation_evidence: 'Detected arrivals',
    automation_attempts: 'Automation attempts',
    automation_ARRIVAL_PICKUP: 'Arrival at pickup',
    automation_ARRIVAL_DROPOFF: 'Arrival at destination',
    automation_PICKUP: 'Passenger pickup',
    automation_COMPLETION: 'Service completion',
    automation_NOTIFYING: 'Sending notice',
    automation_APPLIED: 'Applied',
    automation_CANCELLED: 'Cancelled',
    automation_FAILED: 'Needs operator attention',
    automation_reviewBeforeBooking:
      'An administrator must complete this review before the next booking or service.',
    automation_billingStart: 'Billing starts',
    automation_billingEnd: 'Billing ends',
    automation_closeUnavailable: 'Only administrators can close an active service.',

    reasonCurrentWording: 'Current reason wording',
    traceBillingMixed: 'Some sections included in actual-distance calculation',
    tracePolicyLoading: 'Loading location policy…',
    tracePolicyLoadFailed: 'Could not load location policy.',
    tracePolicySaveFailed: 'Could not save location policy.',

    traceExportCsv: 'Export raw CSV',
    traceExportGeojson: 'Export raw GeoJSON',
    traceExportFailed: 'Export failed or permission is unavailable.',
    traceException: 'Location exception',
    traceGapDetails: 'GPS gap details',
    traceGap_TIME_GAP: 'No continuous GPS data',
    traceGap_IMPLIED_SPEED: 'Abnormal GPS position jump',
    traceGap_UNTRUSTED_POINT: 'Unreliable GPS point',
    traceGapSeconds: '{{count}} seconds',
    traceGapInterval: '{{count}} seconds since the previous point',
    tracePolicy: 'Trip location policy',
    traceRetentionDays: 'Retention for new trips (days)',
    tracePickupRadius: 'Pickup warning radius (m)',
    traceDropoffRadius: 'Drop-off warning radius (m)',
    traceInheritProvider: 'Leave a radius blank to inherit the Provider setting.',
    traceRetentionHelp:
      '30–3650 days. Applies only to new trips; existing expiry dates do not change. Radii: 50–10000 m.',
    traceWarningActivation: 'Status location warnings enabled',
    traceWarningDependency: 'Warning activation awaits compatible event evaluation and Agent support.',
    traceSavePolicy: 'Save location policy',
    tracePolicyInvalid: 'Use whole-number radii from 50–10000 m and retention from 30–3650 days.',
    traceSaving: 'Saving…',
    traceSaved: 'Saved',
    tracePolicyConflict: 'This policy changed. Reload before saving again.',
    traceReloadPolicy: 'Reload policy',

    traceLayers: 'Map layers',
    tracePlanned: 'Planned route',
    tracePlannedShort: 'Plan',
    traceActual: 'Actual route',
    traceActualShort: 'Actual',
    traceEvents: 'Status events',
    traceEventsShort: 'Status',
    traceFit: 'Fit route',
    traceMore: 'More tools',
    traceLegend: 'Actual route phases',
    tracePhasePickup: 'To pickup',
    tracePhaseOutbound: 'Outbound service',
    tracePhaseTransition: 'Transition',
    tracePhaseCare: 'Care',
    tracePhaseReturnWait: 'Return wait',
    tracePhaseReturn: 'Return service',
    traceBillingIncluded: 'Included in actual-distance calculation',
    traceBillingExcluded: 'Not included in actual-distance calculation',
    traceBillingUnknown: 'Actual-distance inclusion not recorded',
    traceLoading: 'Loading actual route…',
    traceFailed: 'Could not load the actual route. Try again.',
    traceAuthFailed: 'Access changed. Location data has been cleared.',
    traceExpired: 'Location evidence has expired. Planned trip information is retained.',
    traceNoTrace: 'No GPS trace was recorded for this trip.',
    traceRetry: 'Retry',
    traceConnected: 'Live connection',
    traceReconnecting: 'Reconnecting · checking for updates',
    traceLastCapture: 'Last GPS capture',
    tracePointCounts: 'GPS points: {{accepted}} accepted · {{returned}} displayed',
    traceCoverageStart: 'Coverage start',
    traceCoverageEnd: 'Coverage end',
    traceMaxGap: 'Maximum GPS gap',
    traceOverrideGps: 'GPS inaccurate',
    traceOverrideMeeting: 'Meeting point incorrect',
    traceOverridePassenger: 'Passenger requested a different point',
    traceOverrideEmergency: 'Emergency',
    traceOverrideOther: 'Other',
    traceOverrideUnknown: 'Unrecognized override reason',
    traceOverrideNote: 'Override note',
    traceTimelineLoading: 'Loading status events…',
    traceTimelineFailed: 'Could not load status events. Try again.',
    traceTimelineEmpty: 'No status events recorded.',
    traceOverflow:
      'This trace exceeds the display limit. Use an authorized raw export or choose another trip.',
    traceQuality: 'Low-quality sample count',
    traceQualityDetails: 'GPS route quality',
    traceQualityReviewTitle: 'Route quality needs review',
    traceQualityHandledTitle: 'Abnormal GPS data has been handled',
    traceQualityUnknownTitle: 'Fare impact cannot be assessed yet',
    traceQualityReviewBody:
      '{{count}} abnormal GPS sections were found. The system leaves them disconnected to avoid displaying or calculating an incorrect route.',
    traceQualityImpactTitle: 'Impact',
    traceQualityMapImpact: 'Route on map',
    traceQualityMapImpactValue: '{{count}} unreliable sections are left disconnected',
    traceQualityDistanceImpact: 'Actual distance',
    traceQualityDistanceGps: 'Calculated only from reliable GPS sections. Excluded sections are not counted.',
    traceQualityDistanceNotGps:
      'Actual distance is not sourced from GPS, so these items do not determine it.',
    traceQualityBillingImpact: 'Fare',
    traceQualityBillingGps:
      'Settlement uses GPS distance. Review the route when there is a distance or fare dispute.',
    traceQualityBillingPlanned: 'Settlement uses planned distance rather than these abnormal GPS sections.',
    traceQualityBillingUnknown: 'The settlement distance source is unavailable. Check the Fare tab.',
    traceQualityDistanceComparison: 'Planned {{planned}} km · measured {{actual}} km',
    traceQualityNextTitle: 'What to do next',
    traceQualityNextReview:
      'If there is a dispute, compare the actual route with the plan and pickup–drop-off events before confirming charges.',
    traceQualityNextNoAction:
      'The system has already excluded unreliable sections. No further action is needed unless there is a route or fare dispute.',
    traceQualityNextUnknown:
      'Check the distance source in the Fare tab. If there is a dispute, compare it with the plan and pickup–drop-off events.',
    traceQualitySummaryTitle: 'Abnormal data summary',
    traceQualityBillingWindowCount:
      '{{count}} sections occurred inside the actual-distance calculation window.',
    traceQualityTechnicalTitle: 'Technical details',
    traceQualityTechnicalHelp: 'Use this when investigating GPS data or sharing evidence with support.',
    traceQualityFilterLabel: 'Anomaly type',
    traceQualityFilterAll: 'All ({{count}})',
    traceQualityTableTime: 'Detected at',
    traceQualityTableType: 'Type / trip stage',
    traceQualityTableMovement: 'Anomaly',
    traceQualityTablePosition: 'GPS position',
    traceQualityInsideBillingWindow: 'Inside actual-distance calculation window',
    traceQualityOutsideBillingWindow: 'Outside actual-distance calculation window',
    traceQualityJumpDistance: 'Distance between points {{distance}}',
    traceQualityImpliedSpeed: 'Approximate speed {{speed}} km/h',
    traceQualityMetersShort: 'm',
    traceQualityKmShort: 'km',
    traceQualityPositionFrom: 'From',
    traceQualityPositionTo: 'To',
    traceQualityAccuracy: 'Accuracy {{from}} → {{to}} m',
    traceNotRecorded: 'Not recorded',
    traceGaps: 'GPS gaps: {{count}}. Lines stop at missing or unreliable evidence.',
    traceAllHidden: 'All route and event layers are hidden.',
    traceEventDetail: 'Status event details',
    traceEventTime: 'Event time',
    traceCaptureTime: 'GPS capture time',
    traceCoordinates: 'Coordinates',
    traceAccuracy: 'Accuracy',
    traceSource: 'Location source',
    traceDirect: 'Direct GPS',
    traceFallback: 'Nearby GPS fallback',
    traceInferred: 'Inferred from nearby GPS',
    traceWarning: 'Warning evaluation',
    traceDistance: 'Distance to target',
    traceRadius: 'Warning radius',
    traceOverride: 'Override reason',
    traceMeters: '{{value}} m',
    traceViewEvent: 'View details',
    traceWarning_NONE: 'No warning',
    traceWarning_OUTSIDE_RADIUS: 'Outside warning radius',
    traceWarning_LOCATION_UNAVAILABLE: 'Location unavailable',
    traceWarning_LOW_ACCURACY: 'Low GPS accuracy',

    routingOutbound: 'Outbound routing',
    routingReturn: 'Return routing',
    routingUnknown: 'Not recorded',

    currentTitle: 'Trips in progress',
    totalTrips: 'Total trips',
    provider: 'Provider',
    serviceGroup: 'Service group',
    serviceArea: 'Service area',
    dateRange: 'Date range',
    unassignedOnly: 'Unassigned',
    jobsCount: '{{n}} jobs',

    // boards (FR-W-BOARD-010)
    boardInProgress: 'In progress',
    boardAdvance: 'Advance',
    staffingQueueFilter: 'Staffing queue ({{n}})',
    staffingStateNOT_STARTED: 'Ready to dispatch',
    staffingStateSEARCHING_AUTO: 'Auto search in progress',
    staffingStateWAITING_MANUAL_ACCEPTANCE: 'Waiting for agent acceptance',
    staffingStateRETRY_NEEDED: 'Retry needed',
    staffingStateREADY: 'Ready to dispatch',
    staffingUrgencyCritical: 'Critical',
    staffingUrgencyWarning: 'Warning',
    staffingUrgencyNormal: 'Normal',
    staffingTimeRemaining: '{{time}} remaining',
    staffingSearchCount: '{{n}} searches',
    staffingDeadline: 'staff by {{time}}',
    boardCompleted: 'Completed',
    boardCancelled: 'Cancelled',
    advanceTitle: 'Advance trips',
    completedTitle: 'Completed trips',
    cancelledTitle: 'Cancelled trips',

    // job actions (FR-W-JOB-010/020)
    reassignTitle: 'Change servicing staff',
    reassignCurrent: 'Current staff: {{name}}',
    sendReassignOffer: 'Send confirmation offer',
    reassignOfferPending:
      'The current staff member remains assigned until the replacement accepts this offer.',
    reassignTripStarted: 'This trip has already started and can no longer be reassigned.',
    offerTaken: 'Another agent has already taken this job. Refresh to see the latest state.',
    offerExpired: 'This offer has expired. Refresh to see the latest state.',
    offerAlreadyResolved: 'This offer has already been resolved. Refresh to see the latest state.',
    editJobTitle: 'Edit job details',
    editPlacesRequired: 'Origin and destination are required',
    editConflict: 'This job was modified by another user. Reload the latest data and edit again.',
    editReload: 'Reload latest',

    jobRef: 'Job ID',
    searchJobRef: 'Search by Job ID',
    colCreated: 'Created at',
    colScheduledFor: 'Scheduled for',
    colPerformed: 'Performed at',
    colCancelledBy: 'Cancelled by',
    colCancelledAt: 'Cancelled at',
    colCancelReason: 'Cancel reason',
    startsInDays: 'in {{n}} days',
    startsToday: 'today',
    colPassenger: 'Passenger',
    colLocation: 'Location',
    colDriver: 'Employee',
    colStatus: 'Status',
    colChat: 'Chat',
    colPassengerName: 'Passenger name',
    driverRole: 'Employee',
    searchingStaff: 'Finding staff',
    dispatchFailedPill: 'Dispatch failed',
    elapsedAgo: '{{t}} elapsed',
    inAnother: 'in {{t}}',
    minutesShort: 'm',
    hoursShort: 'h',

    stDraft: 'Draft',
    stUnassigned: 'Unassigned',
    stScheduled: 'Awaiting start',
    stAssigned: 'Accepted',
    stAwaitingPayment: 'Awaiting payment',
    stEnRoutePickup: 'En route to pickup',
    stArrivedPickup: 'Arrived at pickup',
    stInProgress: 'En route to drop-off',
    stArrivedDropoff: 'Arrived at drop-off',
    stCareGivingStart: 'Care in progress',
    stCareGivingFinish: 'Care finished',
    stReturnWaiting: 'Waiting for return',
    stReturnRiding: 'On the return trip',
    stReturnDropping: 'Arrived (return)',
    stCompleted: 'Completed',
    stAwaitingOverage: 'Awaiting overage payment',
    stCancelled: 'Cancelled',
    stMissed: 'Missed',

    modeRideCare: 'Ride + care',
    modeCare: 'Care',
    modeRide: 'Ride',
    modeTravel: 'Travel',

    aboutTrip: 'About this trip',
    currentStatus: 'Current status',
    updatedAgo: 'Updated {{t}} ago',
    step: 'Step',
    lifecycleElapsed: 'Lifecycle elapsed',
    statusElapsed: 'In this status for {{t}}',
    statusEnteredAt: 'Entered this status at {{time}}',
    statusClosedAt: 'Job closed at {{time}}',
    statusCancelledAt: 'Cancelled at {{time}}',
    statusMissedAt: 'Missed at {{time}}',
    statusAtMeeting: 'At meeting point',
    statusContextDraft: 'This draft has not entered agent assignment.',
    statusContextUnassigned: 'No agent has been assigned.',
    statusContextSearching: 'Searching for an agent.',
    statusContextScheduledReady: 'An agent is assigned; waiting for the scheduled service.',
    statusContextScheduledUnassigned: 'Waiting for agent assignment for this appointment.',
    statusContextAssigned: 'The agent has accepted; waiting to start travel.',
    statusContextTravelToPickup: 'The agent is travelling to the passenger pickup point.',
    statusContextTravelToMeeting: 'The agent is travelling to the meeting point.',
    statusContextAwaitPickup: 'At the pickup point; waiting to pick up the passenger.',
    statusContextArrivedMeeting: 'At the meeting point; waiting to begin care.',
    statusContextTravelToDropoff: 'Taking the passenger to the drop-off point.',
    statusContextBeginCare: 'Waiting for the agent to begin care.',
    statusContextCare: 'Care is in progress.',
    statusContextBeginReturn: 'Waiting for the return journey.',
    statusContextReturnTravel: 'The return journey is in progress.',
    statusContextConfirmFinish: 'Waiting for the agent to confirm service completion.',
    statusContextPaymentPending: 'Waiting for the customer to pay or submit proof.',
    statusContextPaymentProof: 'Payment proof is awaiting operator review; receipt is not yet confirmed.',
    statusContextPaymentRejected: 'Payment proof was rejected; waiting for resubmission.',
    statusContextPaymentUnknown: 'Payment details are unavailable. Review the payment record.',
    statusContextPartnerPayment:
      'Payment is the partner’s responsibility; do not collect from the passenger.',
    statusContextServiceFinished: 'Service has finished; the additional payment is still being resolved.',
    statusContextOverageReview: 'Waiting for the operator to review overage before confirming the amount.',
    statusContextMissed: 'The service did not start. See the timeline for recorded details.',
    statusContextMissedDispatch: 'Agent search ended without an accepted assignment.',
    statusViewPayment: 'View payment',
    statusViewDispatch: 'View dispatch',
    statusPaymentDueBy: 'Payment deadline: {{time}}',
    servicePlanAndResults: 'Service plan and results',
    plannedServiceDuration: 'Planned service time',
    totalDistance: 'Total distance',
    jobDetails: 'Job details',
    creationChannel: 'Creation channel',
    creationChannelWeb: 'Web',
    creationChannelMobileApp: 'Mobile app',
    creationChannelPartnerApi: 'Partner API',
    creationChannelUnknown: 'Unknown',
    originLabel: 'Origin',
    destinationLabel: 'Destination',
    destinationShort: 'Destination',
    pickupAppt: 'Pickup appointment',
    tabMapTimeline: 'Map & timeline',
    tabFare: 'Fare',
    tabChat: 'Chat',
    tabNotes: 'Internal notes',
    tabRatings: 'Ratings',
    options: 'Options',
    confirmPayment: 'Confirm payment',
    changeStaff: 'Change assigned staff',
    cancelTrip: 'Cancel',
    cancelTripTitle: 'Cancel trip',
    cancelReason: 'Reason',
    internalCancellationReason: 'Internal cancellation reason',
    passengerCancellationReason: 'Reason shown to the passenger',
    passengerCancellationReasonFallback: 'This booking was cancelled. Contact the operator for help.',
    staffingQueueActions: 'Staffing queue',
    staffingQueueStart: 'Arrange an agent',
    staffingQueueRetry: 'Retry staffing',
    staffingQueueViewSearch: 'View search',
    staffingQueueDialogLabel: 'Staffing queue for job {{ref}}',
    staffingQueueCloseHint: 'Closing this window does not stop the staffing search.',
    noteLabel: 'Note',
    roundTrip: 'Round trip',
    oneWay: 'One way',
    freeService: 'Free service',
    noFareYet: 'No fare yet',
    noMessages: 'No messages yet',
    noNotes: 'No internal notes yet',
    notesPlaceholder: 'Add an internal note (staff only)…',
    notesAdd: 'Save',
    noRatings: 'No ratings yet',

    // ratings (เรทติ้ง) — FR-W-RATE-010
    ratingGiverPassenger: 'Passenger',
    ratingGiverAgent: 'Agent',
    ratingNoComment: 'No additional comment',
    ratingStarsAria: '{{n}} stars',
    // Positive-tag vocabulary — mirrors the mobile `ratingTag*` l10n keys. An
    // unknown tag is an operator-authored reason label and renders verbatim.
    ratingTagPolite: 'Polite',
    ratingTagProfessional: 'Professional',
    ratingTagPunctual: 'Punctual',
    ratingTagGoodDriving: 'Good driving',
    ratingTagAttentive: 'Attentive',
    ratingTagWellDressed: 'Well dressed',

    // chat (ข้อความ) — FR-W-CHAT-010
    chatSend: 'Send',
    chatSending: 'Sending…',
    chatPlaceholder: 'Type a message…',
    chatAttachImage: 'Attach image',
    chatSystemMessage: 'System message',
    chatPaymentTitle: 'Payment details',
    chatBankName: 'Bank',
    chatAccountNo: 'Account number',
    chatAccountName: 'Account name',
    chatAmount: 'Amount',
    chatDeadline: 'Pay by',
    chatWindowClosed: 'Chat closed (more than 24 hours after the trip completed)',
    chatNoMessages: 'No messages yet',
    chatLoadEarlier: 'Load earlier messages ({{count}})',
    chatImageAlt: 'Chat image',
    chatImageTitle: 'Chat image',
    paid: 'Paid',
    outstanding: 'Outstanding',

    // payment verify queue + records (FR-W-PAY-010 / RULE-PAY-020/030)
    slipQueueBadge: 'Slip to verify',
    amendmentDelivery_BOOKING_AMENDMENT_APPLIED: 'Booking change applied',
    amendmentDelivery_AMENDMENT_OFFER_AVAILABLE: 'Driver offer',
    amendmentDelivery_AMENDMENT_REPLACEMENT_CONSENT: 'Replacement consent',
    amendmentDelivery_AMENDMENT_PAYMENT_REQUIRED: 'Additional payment',
    amendmentDelivery_AMENDMENT_PROOF_REVIEW: 'Payment proof review',
    amendmentDelivery_AMENDMENT_PROOF_REJECTED: 'Proof rejected',
    amendmentDelivery_AMENDMENT_CLOSED: 'Request closed',
    amendmentDelivery_PASSENGER: 'Passenger',
    amendmentDelivery_OPERATOR: 'Operator',
    amendmentDelivery_OFFER_AGENT: 'Offered driver',
    amendmentDelivery_ACCEPTED_AGENT: 'Accepted driver',
    amendmentDelivery_PREVIOUS_AGENT: 'Previous driver',
    amendmentDelivery_RELEASED_AGENT: 'Released driver',
    amendmentDelivery_ACCEPTED: 'Provider accepted',
    amendmentDelivery_NO_DEVICE: 'No registered device',
    amendmentDelivery_DISABLED: 'Transport disabled',
    amendmentDelivery_FAILED: 'Delivery failed',
    amendmentDelivery_SUPPRESSED: 'Delivery suppressed',
    amendmentDelivery_TRANSPORT_LIMIT_REACHED: 'Retry limit or delivery window reached',
    amendmentDelivery_SYNTHETIC_DELIVERY_DISABLED: 'Demo delivery disabled',
    amendmentDelivery_UNKNOWN: 'Unavailable',
    amendmentDelivery_empty: 'No notification deliveries awaiting review',
    amendmentDelivery_title: 'Notification delivery review',
    amendmentDelivery_explanation:
      'Acknowledging closes this delivery review. It does not resend, confirm that the recipient received the message, or change the booking or payment.',
    amendmentDelivery_reason: 'Reason: {{reason}} · Outcome: {{outcome}} · Attempts: {{attempts}}',
    amendmentDelivery_acknowledge: 'Acknowledge delivery issue',
    amendmentDelivery_more: 'More items remain. Complete these reviews to load the next items.',
    amendmentQueue_title: 'Amendment review (all job states)',
    slipQueueFilter: 'Slips to verify ({{n}})',
    paymentKind: 'Payment type',
    paymentKindBase: 'Base fare',
    paymentKindOverage: 'Overage',
    payStatusPendingSlip: 'Awaiting slip',
    payStatusSlipSubmitted: 'Slip to verify',
    payStatusVerified: 'Verified',
    payStatusRejected: 'Slip rejected',
    payStatusWaived: 'Waived',
    payStatusRefundPending: 'Refund pending',
    payStatusRefunded: 'Refunded',
    payCheckGotProof: 'Payment proof received',
    payCheckAmountMatches: 'Amount matches',
    payCheckFoundInAccount: 'Found in account',
    amountDue: 'Amount due',
    amountReceivedLabel: 'Amount received',
    paymentDueBy: 'Pay by',
    paymentRecords: 'Payment records',
    refundAmount: 'Refund amount',
    partnerPaymentManaged: 'Partner payment',
    partnerPaymentManagedBody: 'Managed externally by the partner and provider',
    partnerRefundApproved: 'Approved refund (THB)',
    partnerRefundUndecided: 'No decision recorded',
    partnerRefundPending: 'Refund pending (THB)',
    partnerRefundActual: 'Actually refunded (THB)',
    partnerRecordAmount: 'Amount (THB); decision is total approval, zero means no refund',
    partnerRecordReceipt: 'Record external receipt',
    partnerRecordDecision: 'Record refund decision',
    partnerRecordRefund: 'Record actual external refund',
    refundExternalReference: 'External refund reference',
    refundNote: 'Refund note',
    markRefunded: 'Mark as refunded',
    verificationLog: 'Verification history',
    partialPaymentHint: 'Partial payment — the job stays on hold until it is settled in full',
    waiveRemainder: 'Waive the remainder (release the job)',
    rejectSlip: 'Slip is incorrect',
    rejectSlipTitle: 'Reject payment slip',
    rejectSlipHint: 'The passenger will be asked to resubmit a slip, and the job stays awaiting payment.',
    rejectReasonLabel: 'Reason',
    slipImageAlt: 'Payment slip',
    slipImageTitle: 'Payment slip',
    promptPayQrTitle: 'PromptPay QR',
    promptPayQrAlt: 'PromptPay payment QR code',
    promptPayRecipient: 'Recipient',
    promptPayValue: 'PromptPay destination',
    promptPayGeneratedAt: 'Generated at',
    promptPayVerifyWarning: 'Verify the recipient name in Mobile Banking before paying.',
    promptPayOpenAmountInstruction: 'This QR has no amount. Enter {{amount}} THB in Mobile Banking.',
    promptPayDownload: 'Download PNG',

    colDateTime: 'Date-time',
    colDuration: 'Duration',
    colBy: 'By',
    evCreated: 'Trip created',
    evAssigned: 'Job assigned',
    evAccepted: 'Job accepted',
    evStarted: 'Job started',
    evArrivedPickup: 'Arrived at pickup',
    evPickedUp: 'Passenger picked up',
    evArrivedDropoff: 'Arrived at drop-off',
    evCareStarted: 'Care started',
    evCareFinished: 'Care finished',
    evReturnStarted: 'Return trip started',
    evReturnPickedUp: 'Picked up for return',
    evReturnArrivedDropoff: 'Arrived (return)',
    evCompleted: 'Completed',
    evPaymentVerified: 'Payment verified',
    evPaymentRejected: 'Payment slip rejected',
    evEdited: 'Job details edited',
    evReassigned: 'Staff reassigned',
    evCancelled: 'Job cancelled',
    evAgentWithdrew: 'Assigned agent withdrew',
    evDispatchFailed: 'Dispatch failed — no staff found',
    subCreated: 'Awaiting assignment',
    subAssigned: 'Awaiting acceptance',
    subAccepted: 'Awaiting start',
    subStarted: 'En route to pickup',
    subArrivedPickup: 'Waiting for passenger',
    subPickedUp: 'En route to drop-off',
    actorOfficer: 'Center officer',
    actorAgent: 'Agent',
    actorPassenger: 'Customer',
    actorSystem: 'System',
    actorPartner: 'Partner',
    actorUnknown: 'Unknown actor',
    durationDHM: '{{d}} d {{h}} h {{m}} min',

    createTitle: 'New trip',
    stepOf: 'Step {{a}} of {{b}}',
    passengerInfo: 'Passenger info',
    resetAll: 'Start over',
    recentCalls: 'Recent bookings',
    recentCallsHint: 'Click to reuse the full route',
    searchPassenger: 'Search related passengers by name, or enter an exact phone number',
    addPassengerWithPhone: 'Add a new passenger with phone "{{phone}}"',
    newPassengerTitle: 'New passenger',
    duplicatePhone: 'Phone number already in use',
    bookingReadinessChecking: 'Checking payment status…',
    bookingReadinessUnavailable:
      'Could not confirm whether this customer can book. Refresh before continuing.',
    bookingDebtTitle: 'Booking blocked by an outstanding payment',
    bookingDebtVisibleSummary: 'This customer has {{amount}} THB outstanding with your Provider.',
    bookingDebtHiddenMessage:
      'Unable to create this booking because the customer has an outstanding payment in CabGo. Ask the customer to open CabGo to pay or review its status.',
    bookingDebtMixedMessage:
      'Additional customer action is still required in CabGo. Settling only the payment items shown here may not unblock booking.',
    bookingDebtRefresh: 'Refresh payment status',
    bookingDebtOpenTrip: 'Open trip {{ref}}',
    fareSummary: 'Fare summary',
    fareCalculatedAt: 'Calculated at {{time}}',
    fareUsageSummary: 'Total distance {{distance}} km · total working time {{time}}',
    fareHint: 'As you fill in the form, distance and working time are computed automatically.',
    quoteComputing: 'Calculating fare…',
    quoteRetry: 'Refresh fare',
    quoteOutOfAreaConfirm: 'Confirm out-of-area fare',
    bookingResultUnknown:
      'The booking result is unconfirmed. Check the original booking again before starting a new one.',
    bookingRetryResult: 'Check booking result again',
    bookingSubmitting: 'Checking booking result…',
    bookingOpenOriginal: 'Open original booking',
    bookingRecoveryChecking: 'Checking saved booking information…',
    bookingRecoveryDiscard: 'Discard draft and start again',
    bookingRecoveryInspectTrips: 'Open Trips in another tab',
    bookingRecoveryLoadError:
      'Could not open the saved booking. Retry, or check Trips in another tab before starting another booking.',
    bookingRecoverySaveError:
      'Could not save booking recovery information. Retry before submitting the booking.',
    bookingRecoveryRetry: 'Retry saving or restoring',
    bookingRecoveryRestored:
      'Booking information restored. Review the details and current fare before confirming.',
    bookingRetryMismatchUnresolved:
      'The original booking has conflicting evidence. Open Trips in another tab to check it before starting another booking.',
    quoteRefreshedReview: 'The route and fare were refreshed. Review them and confirm again.',
    confirmRefreshedFare: 'Confirm refreshed fare and book',
    quoteUnavailable: 'Could not obtain a fare. Please try again.',
    quoteRequired: 'Wait for the current fare before booking. Refresh it if it has expired.',
    bookingAdvanceWindow: 'Book up to {{days}} days ahead · latest start {{time}}',
    computingRoute: 'Computing route…',
    routeRetry: 'Route unavailable — retry',
    quoteExpiredRefresh: 'This fare expired. Review the refreshed route and fare before booking.',
    editRouteUnavailable: 'Could not compute the route. Please try again.',
    fareTotals: 'Outbound {{out}} km, return {{back}} km, total {{total}} km. Total working time {{time}}.',
    baseFare: 'Base fare',
    baseFareBasis: 'Includes the first {{km}} km and first {{h}} hours',
    overageDistance: 'Overage - distance',
    overageDistanceBasis: '{{km}} km over × {{rate}} THB/km',
    overageTime: 'Overage - working time',
    overageTimeBasis: '{{actual}} actual hours · billed as {{billed}} h × {{rate}} THB/h',
    distanceFare: 'Distance fare',
    timeFare: 'Working-time fare',
    tierUnitBasis: '{{quantity}} {{unit}} × {{rate}} THB/{{unit}}',
    tierFlatBasis: '{{from}}–{{to}} {{unit}}: {{rate}} THB flat',
    noCharge: 'No charge',
    totalFare: 'Total fare',
    baht: 'THB',
    placeAndTime: 'Place and time',
    meetingPoint: 'Meeting point',
    pricingPackage: 'Service package',
    routeShareLabel: 'Route sharing',
    routeShareAllow: 'Allowed',
    routeShareDeny: 'Not allowed',
    startDateTime: 'Start date & time',
    endDateTime: 'End date & time',
    invalidBookingTime: 'Choose a future start time and an end time after the start.',
    additionalInfo: 'Additional info',
    followerCount: 'Companions',
    none: 'None',
    equipmentLabel: 'Equipment',
    legOut: 'Outbound',
    legBack: 'Return',
    rights: '{{n}} left',
    unlimitedRights: 'Unlimited',
    entitlementExhausted: 'No entitlement left',
    instantBadge: 'Instant',
    bookingTiming: 'Timing',
    bookingInstant: 'Instant',
    bookingAdvance: 'Advance',
    bookingNowHint: 'Starts now',
    bookingRecipientChoice: 'Service recipient',
    roundingNote:
      'Charges are rounded to whole baht, with rounding differences balanced across the line items.',
    bookingRecipientSelf: 'Booking customer receives the service',
    bookingRecipientOther: 'Booking for someone else',
    bookingRecipientName: 'Recipient name',
    bookingRecipientPhone: 'Recipient phone',
    bookingRecipientRequired: 'Enter the recipient’s name and valid phone number',
    bookingRecipientOwnerHint:
      'The booking account receives notifications, pays and uses entitlements under the existing rules',
    bookingAccountLabel: 'Booking customer',
    bookingRecipientLabel: 'Person receiving the service',
    bookingNoteLabel: 'Note',
    bookingNotePlaceholder: 'Additional details for staff (optional)',
    bookOutOfAreaWarning:
      'The pickup is outside this service’s areas. Confirm to book anyway — it will be recorded on the trip’s history.',
    bookOutOfAreaConfirm: 'Book outside the area',

    assignAuto: 'Assign automatically',
    chooseSelf: 'Choose staff manually',
    searchingStaffSpinner: 'Finding staff',
    assignManual: 'Assign manually',
    autoAssign: 'Auto assign',
    confirmAutoTitle: 'Start auto assignment?',
    confirmAutoBody: 'Offers are sent only after you confirm.',
    confirmAutoAssign: 'Confirm auto assignment',
    // confirm assigning a driver who is not currently available (presence ∉ FREE/ON_DUTY)
    confirmAssignTitle: 'Confirm assignment',
    confirmAssignUnavailableBody:
      '{{name}} is not currently available (status: {{status}}). Assign this trip to them anyway?',
    confirmAssignAnyway: 'Assign anyway',
    // MANUAL assign rejected by the hard skill gate (stale data / direct API call)
    assignSkillDenied: 'Cannot assign the job — the agent is missing required skills: {{skills}}',
    assignSkillDeniedGeneric: 'Cannot assign the job — the agent lacks the skills this job requires',
    assignTimeConflict: 'Cannot assign this job because the agent already has a job during this time.',
    assignDriverUnavailable: 'Cannot assign this job because the agent is serving another active trip.',
    assignmentStateChanged: 'The trip status has changed. Please wait while we check its latest state.',
    assignTripAlreadyWon: 'This trip has already been assigned to another agent.',
    candidateTimeConflict: 'Busy during this time',
    candidateTimeConflictRange: '{{start}} – {{end}}',
    editTimeConflict: 'Cannot save because the agent already has another job during this time.',
    // presence labels for the confirm dialog
    presenceOnDuty: 'On duty',
    presenceFree: 'Available',
    presenceBreak: 'On break',
    presenceOffline: 'Offline',
    presenceUnknown: 'Unknown',
    candidateAvailable: 'Available',
    candidateReasonACTIVE_TRIP: 'Active trip',
    candidateReasonSCHEDULE_OVERLAP: 'Schedule overlap',
    candidateReasonSTALE_LOCATION: 'Location heartbeat is stale',
    candidateListUpdated: 'The candidate list changed. Please select an agent again.',
    candidateRefreshFailed: 'Could not refresh agents. Please try again.',
    candidateSelectionStale: '{{name}} is no longer available. Refresh or select another agent.',
    candidateSearchLabel: 'Search staff',
    candidateSearchPlaceholder: 'Name, surname, or part of a phone number',
    candidateSearchScope:
      'Search includes ineligible staff within your access scope, regardless of online and availability filters',
    candidateSearchClear: 'Back to recommendations',
    candidateSearchLoading: 'Searching staff',
    candidateSearchCount: '{{count}} staff found',
    candidateSearchEmpty:
      'No staff match within your access scope. Try another part of the name or phone number.',
    candidateSearchPrevious: 'Previous',
    candidateSearchNext: 'Next',
    candidateSearchSelect: 'Select {{name}}',
    candidateSearchReasons: 'Reasons an offer cannot be sent to {{name}}',
    candidateSearchDetails: 'View eligibility details for {{name}}',
    candidateSearchInactive: 'Inactive',
    candidateSearchCanOffer: 'Offer can be sent — the agent must accept',
    candidateSearchReasonUnknown: 'An additional condition blocks selection. Refresh to check again.',
    candidateSearchReasonEVALUATION_REQUIRED: 'Refresh the evaluation before sending an offer',
    candidateSearchReasonCURRENT_ASSIGNEE: 'Already assigned to this trip',
    candidateSearchReasonINCUMBENT_DRIVER: 'Already assigned to this trip',
    candidateSearchReasonCANDIDATE_REVISION_STALE: 'Details have changed. Refresh the evaluation.',
    candidateSearchUnknown: 'Unavailable',
    candidateSearchCheckedAt: 'Status and schedule checked at {{time}}',
    candidateSearchShortfallUnderMinute: 'Short by less than 1 min',
    candidateSearchMinutes: '{{value}} min',
    candidateSearchEvidenceUnavailable: 'No usable evaluation evidence is available. Please refresh.',
    candidateSearchAmendmentHold: 'Reserved for a booking amendment',
    candidateSearchWorkload: 'Current workload',
    candidateSearchOrigin: 'Evaluated starting point',
    candidateSearchOriginDEVICE_LOCATION: 'Device location',
    candidateSearchOriginHOME_LOCATION: 'Home location used for prediction',
    candidateSearchOriginPREVIOUS_JOB_ASSIGNED: 'Destination of the previous assigned job',
    candidateSearchOriginPREVIOUS_JOB_ACTIVE: 'Destination of the previous active job',
    candidateSearchOriginPREVIOUS_JOB_UNKNOWN: 'Previous job with an unknown destination',
    candidateSearchOriginRECENTLY_COMPLETED: 'Destination of a recently completed job',
    candidateSearchOriginUNKNOWN: 'Evaluated starting point is unknown',
    candidateSearchDistance: 'Distance to pickup / allowed radius',
    candidateSearchTransfer: 'Travel time to pickup',
    candidateSearchBefore: 'Travel from the previous job',
    candidateSearchAfter: 'Travel to the next job',
    candidateSearchTravelComparison: 'Required including buffer: {{required}} · remaining slack: {{slack}}',
    candidateSearchThreshold: 'Score {{score}} / minimum threshold {{threshold}}',
    workloadExpand: 'Show all work',
    workloadCollapse: 'Show less',
    dataAsOf: 'Data as of {{t}}',
    onlineOnly: 'Online only',
    freeOnly: 'Available only',
    suitability: 'Suitability',
    colPlate: 'Plate',
    colDistance: 'Distance',
    colPrevJob: 'Previous job',
    colNextJob: 'Next job',
    shareBadge: 'Share',
    successTitle: 'Your trip is confirmed 🎉',
    scheduledInstant: 'Instant',
    createdAtSummary: 'Created: {{time}}',
    scheduledForSummary: 'Scheduled: {{time}}',
    failTitle: 'Sorry, we could not book this trip right now',
    tryAgain: 'Please try again',
    createReturn: 'Create return trip',
    done: 'Done',
    km: 'km',
    selectPassengerFirst: 'Please select a passenger first',
  },
  dashboard: {
    // left-rail label for the analytics (ChartPie) icon
    menuAnalytics: 'Analytics',
    // page chrome
    realtimeTitle: 'Realtime dashboard',
    analyticsTitle: 'Analytics',
    exportCsv: 'Export CSV',
    totalTrips: 'Total trips',
    totalJobs: 'From all jobs',
    realtimeAsOf: 'Realtime data — {{date}}',
    // filters
    filterProvider: 'Provider',
    filterArea: 'Service area',
    filterGroup: 'Service group',
    filterDate: 'Date',
    filterButton: 'Filters',
    filterSheetTitle: 'Filters',
    applyFilters: 'Apply filters',
    noActiveFilters: 'No filters selected',
    activeFilterSummary: '{{count}} filters active',
    activeFiltersLabel: 'Active filters',
    clearFilter: 'Clear {{name}} filter',
    // realtime sections
    secTripStatus: 'Trip status',
    secDriverStatus: 'Employee status',
    secEfficiency: 'Efficiency',
    secVolume: 'Workload',
    // trip status tiles
    tripEnRouteDropoff: 'En route to drop-off',
    tripEnRoutePickup: 'En route to pickup',
    tripDelayed: 'Delayed',
    tripCompleted: 'Completed',
    tripCancelled: 'Cancelled',
    tripMissed: 'Missed',
    tripScheduled: 'Scheduled',
    tripUnassigned: 'Unassigned',
    // driver status tiles
    driverOnDuty: 'On duty',
    driverFree: 'Free',
    driverOffline: 'Offline',
    driverUnknown: 'Unknown',
    // efficiency tiles
    effOnTime: 'On-time pickup',
    effWorkRate: 'Work rate',
    effJobsPerPerson: 'Jobs per person',
    effAvgRating: 'Average rating',
    unitJobs: 'jobs',
    // volume tiles
    volTotalDistance: 'Total distance',
    volAvgPerJob: 'Avg distance per job',
    volAvgPerPerson: 'Avg distance per person',
    volTotalTime: 'Total working time',
    volAvgWait: 'Avg waiting time',
    volAvgTravel: 'Avg travel time',
    unitKm: 'km',
    unitMin: 'min',
    // analytics — trend chart
    trendTripTime: 'Total trip time',
    trendDistance: 'Total distance',
    trendWorkRate: 'Total work rate',
    trendByType: 'Trips by type',
    trendWait: 'Wait time',
    trendTravel: 'Travel time',
    trendTotal: 'Total',
    // analytics — KPI cards
    cardTotalTrips: 'Total trips',
    cardSuccessCancel: 'Completed & cancelled',
    cardTimeDistribution: 'Time distribution',
    cardOnTimeRate: 'On-time rate',
    cardLeaderboard: 'Employees',
    cardDistanceDistribution: 'Distance distribution',
    cardAreaVolume: 'Volume by area',
    cardTimeOfDay: 'Volume by time of day',
    unitTimes: 'trips',
    labelSuccess: 'completed',
    labelCompleted: 'completed',
    labelCancelled: 'cancelled',
    labelOnTime: 'on-time',
    distInRange: '{{range}}',
    distributionTooltipValue: '{{count}} {{unit}} ({{pct}}%)',
    heatmapTooltip: '{{day}} {{band}}: {{jobs}} {{unit}}',
    dayMon: 'Monday',
    dayTue: 'Tuesday',
    dayWed: 'Wednesday',
    dayThu: 'Thursday',
    dayFri: 'Friday',
    daySat: 'Saturday',
    daySun: 'Sunday',
    // service modes (trips by type)
    modeRideAndCare: 'Ride + care',
    modeCare: 'Care',
    modeRide: 'Ride',
    modeTravel: 'Travel',
    // leaderboard
    colName: 'Name',
    colJobs: 'Jobs',
    // empty states
    noDrivers: 'No employees in this area',
  },

  passengers: {
    reasonCurrentWording: 'Current reason wording',
    title: 'Passengers',
    totalPassengers: 'Total passengers',
    provider: 'Provider',
    passengerGroup: 'Passenger group',
    peopleCount: '{{n}} people',

    // date-range presets
    dateLabel: 'Date',

    // directory columns
    colName: 'Passenger name',
    colProvince: 'Province',
    colPhone: 'Phone',
    colAge: 'Age',
    colTrips: 'Trips',
    colDistance: 'Distance',
    colDuration: 'Duration',
    colRatingReceived: 'Rating received',
    colSpark7: '7-day activity',
    tripsCount: '{{n}} trips',
    tripsLabel: '{{n}} trips',
    noActivity: 'No trips',
    ageYearsMonths: '{{y}}y {{m}}m',

    // summary cards
    cardTrips: 'Total trips',
    cardDistance: 'Total distance',
    cardDuration: 'Total duration',
    cardRating: 'Rating received / given',
    unitTimes: 'trips',
    unitKm: 'km',
    unitHours: 'hours',

    // trip-history columns
    histDate: 'Date',
    histOrigin: 'Origin',
    histDestination: 'Destination',
    histStaff: 'Staff',
    histStatus: 'Status',

    // detail panel
    aboutTitle: 'About this passenger',
    tabOverview: 'Overview',
    tabEntitlements: 'Welfare entitlements',
    sectionPersonal: 'Personal information',
    sectionWelfare: 'Welfare access',
    sectionFavoritePlaces: 'Favorite places',
    sectionFavoriteTrips: 'Favorite trips',
    sectionMobileApp: 'Mobile application',
    ratingWithCount: '{{avg}} ({{count}})',

    // personal-info fields
    gender: 'Gender',
    male: 'Male',
    female: 'Female',
    address: 'Address',
    subDistrictDistrict: 'Sub-district / District',
    province: 'Province',
    postcode: 'Postcode',
    phone: 'Phone number',
    userCode: 'User code',
    memberSince: 'Member since',

    // favorite place kinds
    kindHome: 'Home',
    kindWork: 'Work',
    kindGeneral: 'General',
    viewMap: 'View map',
    viewRoute: 'View route',

    // welfare access
    perWeek: 'per week',
    perMonth: 'per month',
    unlimited: 'Unlimited',
    remainingOf: '{{remaining}} of {{cap}} left',

    // entitlement balance and immutable usage history
    entitlementCurrent: 'Current entitlements',
    entitlementHistory: 'Usage history',
    entitlementTotal: 'Total entitlements',
    entitlementUsable: 'Usable now',
    entitlementExhausted: 'Exhausted',
    entitlementComputedAt: 'Data as of {{value}}',
    entitlementStatusAvailable: 'Available',
    entitlementStatusLow: 'Low',
    entitlementStatusExhausted: 'Exhausted',
    entitlementStatusUnlimited: 'Unlimited',
    entitlementUsedOf: 'Used {{used}} of {{cap}}',
    entitlementRemaining: '{{remaining}} remaining',
    entitlementPeriodRange: '{{from}} to {{to}}',
    entitlementResetAt: 'Resets {{value}}',
    currentDerived: 'Current calculation',
    bookingSnapshot: 'Booking-time snapshot',
    bookingEvidence: 'Booking-time evidence',
    snapshotVersionProvenance: 'Snapshot v{{version}} · {{provenance}}',
    snapshotCapturedAt: 'Captured {{value}}',
    snapshotProviderEvidence: 'Provider: {{name}} · {{id}}',
    snapshotServiceEvidence: 'Service: {{shortName}} · {{fullName}} · {{id}}',
    snapshotModeEvidence: 'Service mode: {{mode}}',
    snapshotStatusBefore: 'Status before booking: {{status}}',
    snapshotLimitingPeriod: 'Limiting period at booking: {{period}}',
    snapshotMatchedGroups: 'Matched groups: {{groups}}',
    snapshotWeeklyEvidence: 'Weekly before booking: cap {{cap}} · used {{used}} · remaining {{remaining}}',
    snapshotMonthlyEvidence: 'Monthly before booking: cap {{cap}} · used {{used}} · remaining {{remaining}}',
    snapshotPeriodEvidence: 'Period {{from}} to {{to}} · reset {{reset}}',
    noneRecorded: 'None recorded',
    limitingPeriod: 'Limiting period',
    matchedGroups: 'Matched passenger groups',
    historyDate: 'Service date',
    historyService: 'Entitlement',
    historyRoute: 'Route',
    historyAgent: 'Agent',
    historyLifecycle: 'Outcome',
    historyMode: 'Service mode',
    modeRideAndCare: 'Ride + care',
    modeCare: 'Care',
    modeRide: 'Ride',
    modeTravel: 'Travel',
    historyTrip: 'Trip',
    historyStatusConsumed: 'Consumed',
    historyStatusRestoredCancelled: 'Restored after cancellation',
    historyStatusRestoredMissed: 'Restored after missed trip',
    restoredAt: 'Restored',
    entitlementUsageAmount: 'Used {{count}} entitlement',
    entitlementConsumedAt: 'Consumed {{value}}',
    entitlementRestoreActor: 'Restored by {{actor}}',
    legacyDerived: 'Legacy record · derived from current data',
    viewTrip: 'View trip',
    noEntitlements: 'No active welfare entitlements',
    noEntitlementHistory: 'No entitlement usage in this period',

    // device
    osVersion: '{{os}} Version: {{version}}',
    appVersion: 'App Version: {{version}}',
    noDevice: 'No app activity yet',

    // create / edit modal
    createTitle: 'Create new passenger',
    editTitle: 'Edit passenger',
    userCodeLabel: 'User code',
    personalInfo: 'Personal information',
    firstName: 'First name',
    lastName: 'Last name',
    dob: 'Date of birth',
    userGroups: 'User groups',
    createButton: 'Create user',
    duplicatePhone: 'Phone number already in use',
    phoneCheckTitle: 'Check phone number',
    phoneCheckHint: 'Enter the complete phone number to search for an existing passenger',
    phoneChecking: 'Checking phone number…',
    existingPassengerHint:
      'This passenger already exists and can be added to the provider directory without duplication.',
    linkButton: 'Add to customer directory',
  },

  drivers: {
    title: 'Employees',
    totalDrivers: 'Total employees',
    provider: 'Provider',
    status: 'Status',
    peopleCount: '{{n}} people',
    driverSubtitle: 'Employee',

    // status filter
    statusActive: 'Allowed',
    statusSuspended: 'Not allowed',
    statusPending: 'Pending approval',
    statusRejected: 'Application rejected',

    // account-status management (approve / suspend / reactivate)
    accountStatus: 'Account status',
    manageStatus: 'Manage',
    manageStatusTitle: 'Manage account status',
    manageStatusHint: 'Set whether {{name}} is allowed to work.',
    rejectionReason: 'Rejection reason',
    rejectionReasonHint: 'Enter a user-safe reason (1–500 characters).',

    // date-range presets
    dateLabel: 'Date',

    // directory columns
    colName: 'Employee name',
    colProvider: 'Provider',
    colPhone: 'Phone',
    colPlate: 'Plate',
    colProvince: 'Province',
    colTrips: 'Trips',
    colCareCount: 'Care jobs',
    colDistance: 'Distance',
    colDuration: 'Duration',
    colRatingReceived: 'Rating received',
    colSpark7: '7-day activity',
    colStatus: 'Status',
    tripsCount: '{{n}} trips',
    jobsCount: '{{n}} jobs',
    unitKm: 'km',
    unitHours: 'hours',

    // presence
    presenceOnDuty: 'On duty',
    presenceFree: 'Available',
    presenceBreak: 'On break',
    presenceOffline: 'Offline',
    presenceUnknown: 'Unknown',

    // center-pane views
    viewLabel: 'View',
    view_today: "Today's trips",
    view_schedule: 'Schedule',
    view_past: 'Past trips',
    view_dashboard: 'Dashboard',
    comingSoon: 'Coming soon',

    // today view
    routeOrigin: 'Origin',
    routeDestination: 'Destination',
    noTripsToday: 'No trips today',
    todayColDate: 'Date-time',
    todayColGroup: 'Service group',
    todayColPassenger: 'Passenger',
    todayColStatus: 'Status',

    // about pane
    aboutTitle: 'About this employee',
    currentStatus: 'Current status',
    updatedAgo: 'updated {{t}} ago',
    driverLocation: 'Employee location',
    todayWorkload: "Today's workload",
    jobProgress: 'Job progress',
    jobStatus: "Today's job status",
    onSchedule: 'On schedule (finishing at {{time}})',
    behindSchedule: 'Behind schedule',
    duration: 'Duration',
    distance: 'Distance',
    nextJob: 'Next job',
    noNextJob: 'None',

    // vehicle / contact / skills
    vehicleInfo: 'Vehicle',
    plate: 'Plate',
    callName: 'Call name',
    makeModel: 'Make / Model',
    noVehicle: 'No vehicle assigned',
    contactInfo: 'Contact',
    email: 'Email',
    province: 'Province',
    licenseNumber: 'License number',
    phone: 'Phone number',
    skills: 'Skills',
    serviceAreas: 'Service areas',
    serviceAreasUnconstrained: 'Not set — works everywhere',
    editAreasTitle: 'Edit service areas',
    editAreasHint: 'Pick the areas this agent works. Select none to work everywhere.',
    skillDrive: 'Drive',
    skillCare: 'Care',

    // self-registration documents (FR-D-AUTH-010 review)
    documents: 'Application documents',
    docProfile: 'Profile photo',
    docLicense: 'Driving licence',
    docVehicle: 'Vehicle photo',
    noDocuments: 'No documents yet',
    documentPolicySnapshot: 'Application policy snapshot: version {{version}}',
    documentStatus_ACTIVE: 'Active',
    documentStatus_DISABLED: 'Disabled',
    documentRevision: 'Revision',
    documentNumber: 'Document number',
    documentIssuedDate: 'Issued date',
    documentExpiryDate: 'Expiry date',
    documentIssuer: 'Issuer',
    documentRevisionHistory: '{{count}} immutable revision(s)',
    bankAccountSection: 'Payout account',
    bankAccountNone: 'No payout account saved',
    bankName: 'Bank',
    otherBankName: 'Other bank name',
    bankAccountName: 'Account name',
    bankAccountNumber: 'Account number',
    bankBookImage: 'Bank-book image',
    bankAccountHistory: '{{count}} immutable revision(s)',
    addBankAccount: 'Add payout account',
    replaceBankAccount: 'Replace payout account',
    viewBankBook: 'View bank-book image',
    disableDocument: 'Disable',
    disableDocumentConfirm: 'Disable this document? Its revision history will be retained.',
    addDocument: 'Add document',
    replaceDocument: 'Replace document',
    replaceDocumentConfirm: 'Replace this document with a new immutable revision?',
    documentType: 'Document type',
    documentImages: 'Document images (JPEG/PNG, up to 5)',
    documentCoverageStart: 'Coverage start date',

    // driver document viewer
    documentViewerTitle: 'Review documents',
    docViewerRotateLeft: 'Rotate left 90°',
    docViewerRotateRight: 'Rotate right 90°',
    docViewerZoomIn: 'Zoom in',
    docViewerZoomOut: 'Zoom out',
    docViewerReset: 'Reset',
    docViewerOpenOriginal: 'Open original in new tab',
    docViewerPrev: 'Previous document',
    docViewerNext: 'Next document',
    docViewerCounter: 'Document {{current}} of {{total}}',
    docViewerNoImage: 'No document image found',
    docViewerLoadingImage: 'Loading image...',
    docViewerAllDocuments: 'All documents',

    // section-edit modals (name/gender field labels reuse the passengers namespace)
    editDriverTitle: 'Edit driver info',
    editSkillsTitle: 'Edit skills',
    editVehicleTitle: 'Edit vehicle info',

    // schedule view
    weekDaySun: 'Sun',
    weekDayMon: 'Mon',
    weekDayTue: 'Tue',
    weekDayWed: 'Wed',
    weekDayThu: 'Thu',
    weekDayFri: 'Fri',
    weekDaySat: 'Sat',
    weekThis: 'This week',
    weekRangeCount: '{{range}} ({{count}})',
    noTripsWeek: 'No trips this week',

    // past trips view
    filterGroup: 'Service group',
    filterStatus: 'Status',
    statusCompleted: 'Completed',
    statusCancelled: 'Cancelled',
    statusMissed: 'Missed',
    kpiTotal: 'Total jobs',
    kpiCompleted: 'Completed',
    kpiCompletionRate: 'Completion rate',
    kpiAvgPerDay: 'Average',
    unitJobs: 'jobs',
    noPastTrips: 'No past trips',

    // dashboard view
    chartLabel: 'Chart',
    metricJobs: 'Total jobs',
    metricDistance: 'Total distance',
    metricDuration: 'Total duration',
    metricRating: 'Total ratings received',
    granDay: 'Daily',
    granMonth: 'Monthly',
    granYear: 'Yearly',
    // segment labels
    segCompleted: 'Completed',
    segCancelled: 'Cancelled',
    segMissed: 'Missed',
    segWithPassenger: 'With passenger',
    segEmpty: 'Empty',
    segIdle: 'Idle',
    segWorking: 'Working',
    segNoRating: 'No rating',
    segS12: '1–2 stars',
    segS3: '3 stars',
    segS4: '4 stars',
    segS5: '5 stars',
    // summary / breakdown derived figures
    sumTotalJobs: 'Total jobs',
    sumTotalDistance: 'Total distance',
    sumAvg: 'Average',
    sumTotalDuration: 'Total duration',
    sumAvgDuration: 'Average duration',
    durationCompactMinutes: '{{minutes}} min',
    durationCompactHours: '{{hours}} hr',
    durationCompactHoursMinutes: '{{hours}} hr {{minutes}} min',
    sumCompletionRate: 'Completion rate',
    sumEfficiency: 'Efficiency',
    sumAvgRating: 'Average',
    unitKmShort: 'km',
    unitStars: 'stars',
    breakdownGroup: 'Service group',
    breakdownTotal: 'Total',
    breakdownAvg: 'Average',
    breakdownRate: 'Completion rate',
    breakdownEfficiency: 'Efficiency',
    breakdownAvgRating: 'Average',
    breakdownShare: '({{share}}%)',
    noDashboardData: 'No data',
  },
  dispatchPolicy: {
    title: 'Dispatch policy',
    scope: 'Policy scope',
    area: 'Service area',
    scopeProvider: 'Provider',
    scopeArea: 'Service area',
    scopeSystem: 'System',
    scopePROVIDER: 'Provider',
    scopeAREA: 'Service area',
    scopeSYSTEM: 'System',
    scopeSource: 'value from: {{scope}}',
    activeVersion: 'Current v{{version}}',
    draftVersion: 'Latest draft v{{version}}',
    saveDraft: 'Save draft',
    activate: 'Activate latest',
    saved: 'A new immutable draft version was saved.',
    activated: 'The latest version is active for new dispatch searches.',
    v3EvidenceRetention: 'Evidence retention (days)',
    v3EditorTitle: 'Dispatch Policy v3 editor',
    v3EditorHint:
      'Edit the complete System policy or a sparse Provider/Area override. Arrays replace inherited values.',
    v3SourceSummary: 'Leaf sources: System {{system}} · Provider {{provider}} · Area {{area}}',
    v3GlobalSettings: 'Global evidence and confirmation',
    v3PreviewTtlMinutes: 'Impact Preview confirmation TTL',
    v3OriginSettings: 'Predictive origin',
    v3RoutingSettings: 'Routing and fallback',
    v3RoutingMode: 'Dispatch routing mode',
    routingModePROVIDER: 'Selected routing provider (Google / Mapbox)',
    routingModeCOORDINATE: 'Coordinate estimate',
    v3RoutingModeHint:
      'Applies only to driver transfers, including travel from the previous job and to the next job. Provider mode uses the globally selected Google / Mapbox provider. Coordinate mode estimates distance and time without external routing; approximate before/after schedule feasibility still applies. Passenger routes, geometry, fares, and passenger-leg offer metrics continue to use the routing provider.',
    v3FeasibilitySettings: 'Schedule feasibility buffers',
    v3NearTermLeadTimeMinutes: 'Near-term scheduled threshold',
    v3PreviousJobContinuityGapMinutes: 'Previous-job continuity gap',
    v3CompletedJobOriginMaxAgeMinutes: 'Completed-job origin maximum age',
    v3DeviceIdealAgeMinutes: 'Device location ideal age',
    v3DeviceDecayUntilMinutes: 'Device confidence decays until',
    v3DeviceDiscardAfterMinutes: 'Discard device location after',
    v3HomeConfidence: 'Home confidence',
    v3AssignedCommitmentConfidence: 'Assigned-job origin confidence',
    v3ActiveCommitmentConfidence: 'Active-job origin confidence',
    v3CompletedCommitmentConfidence: 'Completed-job origin confidence',
    v3UnknownConfidence: 'Unknown-origin confidence',
    v3UnknownTransferEtaMinutes: 'Unknown transfer ETA',
    v3UnknownTravelScore: 'Unknown travel score',
    v3FallbackFeasibilityMultiplier: 'Fallback feasibility multiplier',
    v3RouteConfidenceMultiplier: 'Fallback ranking confidence multiplier',
    v3TotalBudgetMs: 'Total routing budget',
    v3RequestTimeoutMs: 'Routing request timeout',
    v3BatchSize: 'Route batch size',
    v3Concurrency: 'Concurrent route batches',
    v3MaxRetries: 'Route retry rounds',
    v3BeforeMinimumMinutes: 'Minimum buffer before',
    v3BeforeEtaPercent: 'ETA buffer before',
    v3AfterMinimumMinutes: 'Minimum buffer after',
    v3AfterEtaPercent: 'ETA buffer after',
    v3WindowBeforeMinutes: 'Workload window before pickup',
    v3WindowAfterMinutes: 'Workload window after pickup',
    v3PriorSuccesses: 'Acceptance prior successes',
    v3PriorSampleSize: 'Acceptance prior sample size',
    v3PriorRating: 'Rating prior mean',
    v3PriorReviewCount: 'Rating prior review count',
    v3TieTolerancePoints: 'Tie tolerance',
    warningConfirmationTitle: 'This policy has warnings to review',
    confirmWarning: 'Acknowledge warning and continue',
    warningDistanceSaturation:
      '{{profile}}: the worst distance-factor value is below the search radius, saturating distance scores for eligible candidates',
    warningDistanceBufferPercent: 'Distance percentage buffer exceeds 100%',
    warningEtaBufferPercent: 'ETA percentage buffer exceeds 100%',
    warningFixedDistance: 'Fixed distance buffer exceeds 50 km',
    warningFixedTime: 'Fixed time buffer exceeds 180 minutes',
    copyId: 'Copy ID',
    idCopied: 'ID copied',
    unknownName: 'Name unavailable',
    inherit: 'Use default',
    enabled: 'Enabled',
    disabled: 'Disabled',
    strategyDefaults: 'Strategy defaults',
    strategyNow: 'NOW / instant',
    strategyScheduled: 'Scheduled',
    strategyNOW: 'NOW / instant',
    strategySCHEDULED: 'Scheduled',
    strategySequential: 'Sequential',
    strategyBroadcast: 'Broadcast',
    strategySEQUENTIAL: 'Sequential',
    strategyBROADCAST: 'Broadcast',
    searchLifecycle: 'Search, routing, and offer lifecycle',
    fanout: 'Broadcast fanout',
    redispatchAttempts: 'Redispatch attempts',
    offerTimeoutSec: 'Offer timeout (seconds)',
    reofferRejectedCooldownSec: 'Reject re-offer cooldown (seconds)',
    radiusKm: 'Search radius (km)',
    etaRoadFactor: 'Fallback road factor',
    etaAverageSpeedKph: 'Fallback average speed (km/h)',
    etaNoCoordinateMinutes: 'No-coordinate ETA (minutes)',
    buffers: 'Distance and time buffers',
    distancePercent: 'Distance percentage',
    distanceFixedKm: 'Fixed distance (km)',
    etaPercent: 'Time percentage',
    etaFixedMinutes: 'Fixed time (minutes)',
    candidateInclusion: 'Candidate inclusion',
    sequentialRequiresOnline: 'Sequential requires online',
    freshnessCutoffMinutes: 'Fresh-location cutoff (minutes)',
    sequentialIncludeStaleLocation: 'Sequential includes stale location',
    sequentialIncludeNoLocation: 'Sequential includes no location',
    broadcastIncludeOffline: 'Broadcast includes offline',
    broadcastIncludeStaleLocation: 'Broadcast includes stale location',
    broadcastIncludeNoLocation: 'Broadcast includes no location',
    rankingFactors: 'Ranking factors',
    factorPRESENCE: 'Presence',
    factorLOCATION_FRESHNESS: 'Location freshness',
    factorDISTANCE: 'Distance',
    factorPICKUP_ETA: 'Pickup ETA',
    factorSCHEDULE_SLACK_BEFORE: 'Schedule slack before',
    factorSCHEDULE_SLACK_AFTER: 'Schedule slack after',
    factorWORKLOAD: 'Workload',
    factorLAST_ASSIGNED_AGE: 'Last assigned age',
    factorACCEPTANCE_RATE: 'Acceptance rate',
    factorRATING: 'Rating',
    factorORIGIN_CONFIDENCE: 'Origin confidence',
    factorEnabled: 'Enabled',
    factorWeight: 'Weight',
    factorIdeal: 'Ideal',
    factorWorst: 'Worst',
    factorThreshold: 'Threshold (blank means none)',
    factorMissingScore: 'Missing-data score',
    factorPenaltyScore: 'Penalty score',
    lookbackDays: 'Lookback (days)',
    tieBreakers: 'Ordered tie-breakers',
    tiePICKUP_ETA: 'Lowest pickup ETA',
    tieWORKLOAD: 'Lowest workload',
    tieLAST_ASSIGNED_AGE: 'Oldest last assignment',
    tieDISTANCE: 'Lowest distance',
    tieORIGIN_CONFIDENCE: 'Highest origin confidence',
    tieSCHEDULE_SLACK_BEFORE: 'Most slack before',
    tieSCHEDULE_SLACK_AFTER: 'Most slack after',
    tieACCEPTANCE_RATE: 'Highest acceptance rate',
    tieRATING: 'Highest rating',
    tieDRIVER_ID: 'Driver ID',
    driverIdLast: 'Driver ID is always appended as the final deterministic tie-breaker.',
    moveUp: 'Move up',
    moveDown: 'Move down',
    remove: 'Remove',
    addTie: 'Add {{name}}',
    simulatorTitle: 'Current vs Draft simulator',
    simulatorHint: 'Read-only: no offers, notifications, audit events, status changes, or policy saves.',
    resolutionMode: 'Policy simulation mode',
    resolutionModeScopeOnly: 'Test the scope being edited',
    resolutionModeScopeOnlyHint:
      'Compares this scope’s active policy with its draft without child scopes, such as Area, overriding it.',
    resolutionModeEffective: 'Actual trip behavior',
    resolutionModeEffectiveHint: 'Uses real Area > Provider > System precedence for the trip pickup.',
    purpose: 'Simulation purpose',
    strategy: 'Dispatch strategy',
    purposeInitial: 'Initial dispatch',
    purposeReassignment: 'Reassignment',
    trip: 'Real trip',
    simulate: 'Run simulation',
    dataAsOf: 'Data as of {{value}}',
    current: 'Current',
    draft: 'Draft',
    delta: 'Delta',
    deltaAdded: 'Included only in Draft: {{value}}',
    deltaRemoved: 'Removed by Draft: {{value}}',
    fanoutValue: 'Max pending {{count}}',
    budgetValue: 'Offer budget {{count}}',
    valuesUsed: 'Effective values used',
    simulationInputSource: 'Source: simulation input',
    rankingValuesUsed: 'Ranking factor values used',
    factorUsedValue:
      'On · weight {{weight}}% · ideal {{ideal}} {{unit}} · worst {{worst}} {{unit}} · threshold {{threshold}} {{unit}}',
    factorNotUsed: 'Off — excluded from score calculation',
    roundValue: 'Audit round {{count}}',
    noCandidates: 'No included candidates',
    included: 'Included',
    excluded: 'Excluded',
    scoreValue: 'Score {{score}}',
    reasons: 'Reasons',
    routeSource: 'Route source',
    routeSourceROUTING: 'Live route estimate',
    routeSourceROUTE_PROVIDER: 'Live route estimate',
    routeSourceHAVERSINE_FALLBACK: 'Estimated from straight-line distance',
    routeSourceESTIMATED_FALLBACK: 'Coordinate estimate after provider routing failure',
    routeSourceCOORDINATE_ESTIMATE: 'Intentional coordinate estimate',
    routeSourceCONSERVATIVE_FALLBACK: 'Conservative estimate',
    routeSourceUNKNOWN: 'Unknown',
    presenceFREE: 'Available',
    presenceON_DUTY: 'On duty',
    presenceBREAK: 'On break',
    presenceOFFLINE: 'Offline',
    minutesShort: 'min',
    factorEvidence:
      'raw {{raw}} · normalized {{normalized}} · weight {{weight}} · contribution {{contribution}} · threshold {{threshold}}',
    helpLabel: 'Explanation',
    helpExample: 'Example',
    helpHowTitle: 'How it is calculated',
    helpImpactTitle: 'Impact of changing this',
    helpFormulaTitle: 'Formula',
    scoreFormula:
      'Factor score = penalty + (measured value clamped between ideal–worst → scaled 0–100) × (100 − penalty) ÷ 100\n\nTotal score = Σ (factor score × weight ÷ 100) — the highest total ranks #1',
    helpClose: 'Got it',
    unitKm: 'km',
    unitMinutes: 'min',
    unitSeconds: 'sec',
    unitHours: 'h',
    unitDays: 'days',
    unitPercent: '%',
    unitDrivers: 'drivers',
    unitTrips: 'trips',
    unitRounds: 'rounds',
    unitTimes: '×',
    unitKph: 'km/h',
    unitPoints: 'pts',
    unitRating: 'stars',
    unitMilliseconds: 'ms',
    unitRoutes: 'routes',
    dirLowerBetter: 'lower is better ({{unit}})',
    dirHigherBetter: 'higher is better ({{unit}})',
    dirCategorical: 'categorical (online = best)',
    fanoutHelp:
      'Description: max agents who receive a broadcast offer in one round.\n\nHow: candidates are ranked by total score, then offers go to the top N.\n\nExample: 3 → ranks #1–3 are offered together; first to accept wins, the rest expire.\n\nImpact: higher = fills faster but many agents get wasted notifications; lower = calmer but slower.',
    radiusKmHelp:
      'Description: max distance from pickup to count as a candidate.\n\nHow: measured from the agent’s latest location to the pickup (road distance if available, else straight-line); beyond this = excluded.\n\nExample: 10 → 8 km away is in, 12 km away is out.\n\nImpact: higher = easier to find someone but longer ETAs; lower = fast trips but risks "no driver found".',
    offerTimeoutSecHelp:
      'Description: how long an offer waits for an answer.\n\nHow: counts from the moment the offer is sent; no acceptance by the deadline = expired, next round starts immediately.\n\nExample: 45 → sent at 10:00:00, no accept by 10:00:45 → next round.\n\nImpact: higher = time to decide but trips start slower; lower = fast rounds but agents may miss notifications.',
    reofferRejectedCooldownSecHelp:
      'Description: how long an agent who explicitly rejected this trip stays out of later searches for the same trip context.\n\nTimeouts and operator-stopped offers do not start this cooldown.\n\nDefault: 1,800 seconds (30 minutes).',
    redispatchAttemptsHelp:
      'Description: max rounds before declaring "no driver found".\n\nHow: each round offers to the next group by rank; exhausted = trip waits for manual assignment.\n\nExample: fanout 3 × 4 rounds = up to 12 agents tried.\n\nImpact: higher = better odds but passengers wait longer; lower = quick outcome but more manual work.',
    etaRoadFactorHelp:
      'Description: multiplier converting straight-line to road distance when no real route exists.\n\nHow: road distance ≈ haversine × factor.\n\nExample: 10 km × 1.3 ≈ 13 km.\n\nImpact: higher = longer ETAs, distant agents penalized; lower = optimistic, risks underquoting ETA.',
    etaAverageSpeedKphHelp:
      'Description: assumed speed for ETA when no real route exists.\n\nHow: ETA (min) = distance (km) ÷ speed (km/h) × 60.\n\nExample: 13 ÷ 30 ≈ 26 min.\n\nImpact: higher = shorter ETA, distant agents viable; lower = conservative, suits congested areas.',
    etaNoCoordinateMinutesHelp:
      'Description: fixed ETA used when an agent has no location.\n\nHow: used directly instead of distance-based calculation.\n\nExample: 8 → an agent with GPS off is assumed 8 minutes away, always.\n\nImpact: higher = no-location agents rank poorly; lower = they compete with nearby located agents.',
    strategyHelp:
      'Description: how offers are handed out each round.\n\nSequential: rank #1 only; on timeout the next rank — offer budget = redispatch rounds.\n\nBroadcast: several agents per "fanout" — offer budget = fanout × rounds.\n\nExample: fanout 3 + 4 rounds → Broadcast max 12 offers / Sequential 4 offers.\n\nImpact: Broadcast is faster but burns budget faster and notifies losing agents; Sequential is slower but fairer to agents.',
    bufferHelp:
      'Description: distance/time safety margins on estimated routes.\n\nHow: used value = computed × (1 + percent) + fixed.\n\nExample: ETA 20 min + 10% + 5 min = 27 min.\n\nImpact: higher = safer but lower daily capacity; lower = denser scheduling but risk of lateness.',
    inclusionHelp:
      'Description: who may enter the candidate pool before ranking.\n\nHow: hard filters — failing any condition = excluded outright (not just scored lower).\n\nExample: "Sequential requires online" on → offline agents are cut before scoring.\n\nImpact: more filters = narrower pool but only truly available agents; fewer = wider pool but risk offering to app-closed/stale-location agents.',
    factorPRESENCEHelp:
      'Description: scores by availability status.\n\nHow: FREE/ON_DUTY → "ideal"; anything else → "penalty score".\n\nExample: ideal 100, penalty 30, weight 10 → online contribution 10.0 vs offline 3.0.\n\nImpact: higher weight = jobs consistently go to agents who are truly online.',
    factorLOCATION_FRESHNESSHelp:
      'Description: age of the agent’s latest location (minutes).\n\nHow: age = now − last fix; linearly scaled ideal→worst; beyond worst = penalty score.\n\nExample: ideal 1 / worst 30 → 15 min old scores ~50/100; 45 min = penalty.\n\nImpact: higher weight = trusts fresher locations, reducing "nearby but stale" mistakes; weak-connectivity agents may be unfairly penalized.',
    factorDISTANCEHelp:
      'Description: distance from agent to pickup (km).\n\nHow: real route if available, else straight-line × road factor; linearly scaled ideal→worst.\n\nExample: ideal 0 / worst 10, weight 15 → 4 km scores 60/100 → contribution 9.0.\n\nImpact: higher weight = nearest agent always wins (saves time/fuel) but nearby agents get repeated jobs while distant ones starve.',
    factorPICKUP_ETAHelp:
      'Description: estimated minutes to the pickup point.\n\nHow: ETA = distance ÷ speed (or real route) + buffers; scaled like distance.\n\nExample: ideal 0 / worst 60, weight 20 → ETA 12 min scores 80/100 → contribution 16.0.\n\nImpact: higher weight = passengers wait less — usually the heaviest factor since it hits passenger experience most directly.',
    factorSCHEDULE_SLACK_BEFOREHelp:
      'Description: free minutes before this trip starts.\n\nHow: slack = start − previous job’s end (no previous job = plenty).\n\nExample: ideal 120, weight 15 → 60 min slack scores 50/100 → 7.5.\n\nImpact: higher weight = avoids tightly-scheduled agents, reducing late arrivals caused by earlier jobs.',
    factorSCHEDULE_SLACK_AFTERHelp:
      'Description: free minutes after this trip ends.\n\nHow: slack = next job’s start − this end (including travel to the next job).\n\nExample: ideal 120, weight 15 → 30 min slack scores 25/100 → 3.75.\n\nImpact: higher weight = avoids back-to-back pressure and risky time management.',
    factorWORKLOADHelp:
      'Description: trip count in the lookback window — balances load.\n\nHow: counts trips in lookbackDays; 0 trips = full score, at "worst" = 0.\n\nExample: ideal 0 / worst 5, weight 5 → 2 trips scores 60/100 → 3.0.\n\nImpact: higher weight = fairer income distribution but may assign a farther agent because the near one is already busy.',
    factorLAST_ASSIGNED_AGEHelp:
      'Description: hours since the agent’s last assignment — idle agents get priority.\n\nHow: age = now − last assignment; idle for "ideal" hours = full score.\n\nExample: ideal 24, weight 4 → 12 h idle scores 50/100 → 2.0.\n\nImpact: higher weight = rotating queue (high fairness) but may ignore proximity.',
    factorACCEPTANCE_RATEHelp:
      'Description: share of offers accepted (%) in the lookback window.\n\nHow: rate = accepted ÷ (accepted+rejected+expired); never offered = "missing-data score".\n\nExample: 8/10 = 80%, weight 3 → 2.4.\n\nImpact: higher weight = jobs go to reliable accepters; new agents or those with past rejections fall behind.',
    factorRATINGHelp:
      'Description: average passenger rating (1–5 stars).\n\nHow: mean rating; no history = "missing-data score"; below "worst" = penalty.\n\nExample: ideal 5 / worst 1, weight 3 → 4.8★ scores 95/100 → 2.85.\n\nImpact: higher weight = jobs go to top-rated agents but newcomers struggle — keep "missing-data score" reasonably high.',
    purposeHelp:
      'Initial dispatch (INITIAL): simulate finding an agent for a new trip.\n\nReassignment: simulate replacing the current agent — the incumbent is always removed from the candidate pool.\n\nImpact: pick the view matching the real situation, e.g. replacing the agent of an assigned trip requires REASSIGNMENT to see the successor.',
    tripPickerTitle: 'Choose a real trip',
    tripPickerSearch: 'Search: trip ref, passenger name/phone, pickup-dropoff…',
    tripPickerEmpty: 'No trips match',
    tripPickerHint: 'Reassignment requires a scheduled trip that already has an agent.',
    tripOptionalHint: 'Optional — select a real trip only to prefill the details below.',
    factorContributionTitle: 'Score split by factor (raw → contribution)',
    factorContributionNote:
      'Bars show each factor’s share of the total score · red = negative · only enabled factors are shown.',
    reasonOffline: 'Offline — Broadcast does not include offline agents',
    reasonStaleLocation: 'Location older than the freshness cutoff',
    simMode: 'Simulated trip',
    simModeReal: 'Real trip',
    simModeHypothetical: 'Custom details',
    hypoHint:
      'Simulates a NEW trip with these details — nothing is created. A selected real trip only prefills the details; reassignment still uses it to identify the incumbent agent.',
    hypoPrefill: 'Fill from selected trip',
    hypoServiceGroup: 'Service group',
    hypoPickup: 'Pickup (lat, lng)',
    hypoDropoff: 'Dropoff (lat, lng)',
    hypoTiming: 'Timing',
    hypoInstant: 'Instant',
    hypoScheduled: 'Scheduled at',
    purposeLockedHint: 'This trip already has an agent — only reassignment can be simulated for it.',
    purposeInitialLockedHint: 'This trip has no agent yet — only initial dispatch can be simulated for it.',
    reasonTARGET_TRIP_INELIGIBLE:
      'The trip cannot be dispatched in this mode (wrong purpose for its assignment state)',
    reasonMISSING_REQUIRED_SKILLS: 'Missing required skills for this service group',
    reasonPROVIDER_MISMATCH: 'The agent belongs to a different provider',
    reasonDRIVER_INACTIVE: 'The agent account is not active',
    reasonSKILLS_MISMATCH: 'Missing required skills for this service group',
    reasonPRESENCE_REQUIRED: 'The policy requires an online, available agent',
    reasonLOCATION_REQUIRED: 'No usable location is available for dispatch checks',
    reasonLOCATION_STALE: 'The latest location is older than the allowed limit',
    reasonSERVICE_AREA_MISMATCH: 'The agent service areas do not cover the selected trip area',
    reasonTARGET_NOT_DISPATCHABLE: 'The trip or service group is not ready for dispatch',
    reasonBASE_GATE_FAILED: 'The agent failed a base dispatch eligibility check',
    reasonINVALID_TRIP_SCHEDULE: 'The trip start time or duration is invalid',
    reasonDISTANCE_OUT_OF_RANGE: 'Outside the configured search radius',
    reasonACTIVE_JOB_CONFLICT: 'An active job conflicts with this trip',
    reasonTIME_OVERLAP: 'The trip overlaps another assigned job',
    reasonTRANSFER_ETA_TOO_LONG: 'Travel time and buffer do not fit before or after the trip',
    reasonOFFLINE_NOT_INCLUDED: 'The policy excludes offline agents',
    reasonSTALE_LOCATION_NOT_INCLUDED: 'The policy excludes stale locations',
    reasonNO_LOCATION_NOT_INCLUDED: 'The policy excludes agents without a location',
    reasonTHRESHOLD_NOT_MET: 'At least one ranking factor is below its threshold',
    reasonROUTING_FALLBACK: 'No live route — ETA estimated from straight-line distance',
    reasonREJECT_COOLDOWN: 'Agent explicitly rejected this trip during the configured cooldown',
  },
  dispatch: {
    evidenceExpired: 'Evidence expired',
    // page
    title: 'Offer lifecycle lab',
    subtitle: 'Dev/QA tool — inspect persisted offers and exercise accept, reject, and timeout transitions',
    devOnly: 'This page is available in non-production environments only',
    unassignedTrips: 'Trips awaiting an agent',
    tripsCount: '{{n}} trips',
    selectTripHint: 'Select a trip from the list to view its offers',
    noUnassigned: 'No trips awaiting an agent',

    // list columns
    colTrip: 'Trip',
    colPassenger: 'Passenger',
    colRoute: 'Route',

    // detail header
    tripRef: 'Trip ref',
    status: 'Status',
    strategy: 'Strategy',
    strategySequential: 'Sequential',
    strategyBroadcast: 'Broadcast',
    strategyPolicyDefault: 'Use dispatch policy default',
    advancedOptions: 'Advanced options',
    searching: 'Searching for an agent',
    idle: 'No pending offers',
    searchFailed: 'Dispatch failed — no staff found',
    failedAt: 'Failed at {{time}}',
    failureTimeLoading: 'Loading dispatch failure time',
    failureTimeUnavailable: 'Dispatch failure time could not be loaded',
    assignedTo: 'Assigned to',

    // start
    startDispatch: 'Start searching for an agent',
    noOffersYet: 'No offers sent yet',
    startHint: 'Press the button to begin auto-dispatch (send the request to agents)',

    // offer card
    offers: 'Offers sent',
    offersCount: '{{n}} offers',
    rank: 'Rank',
    round: 'Round',
    plate: 'Plate',
    distance: 'Distance',
    expiresIn: 'Expires in',
    secondsShort: 's',
    timedOut: 'Timed out',

    // offer statuses
    statusPending: 'Pending',
    statusAccepted: 'Accepted',
    statusRejected: 'Rejected',
    statusExpired: 'Expired',
    statusSuperseded: 'Superseded',
    statusCancelled: 'Cancelled',

    // actions
    accept: 'Accept',
    reject: 'Reject',
    tick: 'Simulate timeout / Tick',
    rejectNote: 'Simulated agent rejection',
    loadError: 'Failed to load offers',

    // operator-facing (trip detail card + create-trip modal)
    cardTitle: 'Dispatch',
    viewHistory: 'View history',
    assigned: 'Agent assigned',
    offerMethod: 'Offer method',
    historyTitle: 'Dispatch history',
    historyOffersTab: 'Sent offers',
    historyEvaluationsTab: 'Staff evaluations',
    historyEvaluationHint:
      'Reasons saved at evaluation time. Names and phone numbers use current account details. Eligibility is never recalculated here.',
    historyAttemptFilter: 'Evaluation revision',
    historyAttemptOption: 'Revision {{revision}} · {{time}}',
    historyEvaluatedAt: 'Evaluated at {{time}}',
    historyEvaluationCount: '{{count}} matching evaluations in this revision',
    historyEvaluationLoading: 'Loading saved evaluations',
    historyEvaluationRetry: 'Reload saved evaluations',
    historyEvaluationMissing:
      'No saved evaluation exists for this search. The reason an offer was not sent is unavailable.',
    historyEvaluationEmpty:
      'No saved evaluation matches this query in this revision. Missing evidence does not mean the employee was ineligible.',
    historyAttemptsLimited: 'Only the most recent evaluation revisions are listed within the system limit.',
    historyOutcomeEVIDENCE_UNAVAILABLE: 'Evaluation evidence unavailable',
    historyEvidenceUnavailableHint:
      'The saved record does not contain enough evidence to establish the original decision or reasons.',
    historyOutcomeEXCLUDED: 'Excluded at evaluation time',
    historyOutcomeOFFERED: 'Linked offer recorded',
    historyOutcomeELIGIBLE_NOT_OFFERED: 'Eligible; no linked offer',
    historyOutcomeEVIDENCE_EXPIRED: 'Evaluation evidence expired',
    historyNoOfferCauseUnknown:
      'Passed this evaluation, but no linked offer was recorded. A specific reason for not sending was not recorded.',
    historyEvidenceExpiredHint:
      'The saved evidence has expired. The original eligibility decision and reasons are unavailable.',
    historyUnknownReason: 'Recorded reason: {{reason}}',
    historyEvaluationDetails: 'Evaluation details for {{name}}',
    historyEvaluationScore: 'Saved score',
    historyEvaluationId: 'Evaluation ID',
    historyUnknownTrigger: 'Saved evaluation',
    historyTriggerINITIAL: 'Initial evaluation',
    historyTriggerRETRY: 'Retry evaluation',
    historyTriggerREFILL: 'Refill evaluation',
    historyTriggerMANUAL_REFRESH: 'Operator requested a new evaluation',
    historyClearSearch: 'Clear search',
    historyEmployeeMatches: '{{count}} matching offers in the selected searches and waves',
    historyEmployeeEmpty: 'No employee offer history matches your search in the selected searches and waves',
    historySearchFilter: 'Search invocation',
    historySearchNumber: 'Search {{n}}',
    historyAllSearches: 'All · {{n}} searches',
    historyAllHint: 'Grouped by search and wave, including searches with no offers.',
    historyWaveFilter: 'Offer wave',
    historyAllWaves: 'All waves',
    historyWaveCount: 'Wave {{n}} · {{count}} offers',
    historyTimezone: 'Bangkok time (UTC+7) · Rank ascending',
    historyModeAUTO: 'Automatic',
    historyModeMANUAL: 'Manual',
    historyPurposeINITIAL: 'Initial assignment',
    historyPurposeREASSIGNMENT: 'Reassignment',
    historyStateACTIVE: 'Active',
    historyStateASSIGNED: 'Agent accepted',
    historyStateEXHAUSTED: 'No agent accepted',
    historyStateCANCELLED: 'Cancelled',
    historyStateSUPERSEDED: 'Superseded',
    historyLegacy: 'Legacy history',
    historyLegacyHint: 'Historical offers; original search context is unavailable.',
    historyStarted: 'Started',
    historyEnded: 'Ended',
    historyEmployee: 'Employee',
    historySentExpires: 'Sent / Expires',
    historyResult: 'Result / Recorded time',
    historySent: 'Sent',
    historyExpires: 'Expires',
    historyWorkerResponse: 'Employee response',
    historySystemResult: 'System result recorded',
    historyDetails: 'Details',
    historyOfferId: 'Offer ID',
    historyReasonNote: 'Recorded reason note',
    historyNoSearches: 'No dispatch searches recorded',
    historySentDisclaimer:
      'Sent is the time the offer was created in the system, not a delivery or read receipt.',
    historyMissingTime: '— means no recorded value; expiry deadlines are not response times.',
    staffingSearchExhausted: 'No agent accepted this search',
    staffingSearchExhaustedHint:
      'The booking remains in the staffing queue. Retry automatically, choose an agent, or continue later.',
    doLater: 'Do later',
    stopReasonPrompt: 'Why are you stopping this active search?',
    stopConfirm: 'Stop this search and switch dispatch mode?',
    activeSearchConflict: 'Another dispatch search is active',
    activeSearchConflictHint: 'Stop the current search before starting the selected strategy.',
    stopSearchAndRetry: 'Stop current search and retry',
    rejectCooldownOverrideReason: 'Why should this agent be offered the trip again during the cooldown?',
    rejectCooldownOverrideConfirm: 'Override the Reject cooldown for this agent?',
  },
  tracking: {
    title: 'Live tracking',
    asOf: 'Updated {{time}}',

    // view switch (FR-W-LIVE-010: Current operations / Map view / Driver view)
    viewLabel: 'View',
    viewToday: 'Current operations',
    viewMap: 'Map view',
    viewAgents: 'Agent view',

    // left rail — agents by presence
    tabWorking: 'Working',
    tabFree: 'Available',
    tabOffline: 'Offline',
    noAgents: 'No agents in this state',
    todayShort: 'Today',

    // left rail — current operational jobs
    noJobsToday: 'No current operations',

    // right panel — job queues
    ongoing: 'Ongoing',
    unassigned: 'Unassigned',
    noOngoing: 'No ongoing jobs',
    noUnassigned: 'No unassigned jobs',
    openJob: 'Open detail',
    queueButton: 'Jobs {{count}}',
    queueTitle: 'Operational jobs',

    // right panel — selected agent live detail
    backToQueues: 'Back to job queues',
    viewProfile: 'View profile',
    currentJob: 'Current job',

    // location freshness (FR-W-LIVE-010 / issue #480)
    noLocationData: 'No location data',
    locationStale: 'Location stale',
    staleMarkerTooltip: 'Location stale (last updated {{time}})',
    staleLocationWithTime: 'Location stale · Updated {{time}}',
  },
  reports: {
    ...reportBaseTranslations.en,
    ...cancelledServicesTranslations.en,
    ...dailyDriverServiceTranslations.en,
    ...dailyServiceFrequencyTranslations.en,
    ...driverAssignmentDetailTranslations.en,
    ...missedServicesTranslations.en,
    ...serviceFrequencyTodayTranslations.en,
    ...serviceUsersTranslations.en,
    navLabel: 'Reports',
    title: 'Reports Hub',
    serviceJobDetail: 'Service job detail',
    cancelledServices: 'Cancelled services',
    missedServices: 'Missed services',
    dailyServiceFrequency: 'Daily service frequency',
    serviceFrequencyToday: "Today's service frequency",
    dailyDriverService: 'Daily driver service',
    driverAssignmentDetail: 'Driver assignment detail',
    serviceUsers: 'All service users',
    serviceJobDetailDesc: 'One row per Trip for fare and payment reconciliation',
    reportList: 'All reports',
    dateBasis: 'Service-date basis',
    dataAsOf: 'Data as of {{date}}',
    dictionary: 'Data dictionary',
    columns: 'Columns',
    columnsTitle: 'Visible columns',
    columnsIntro: '{{count}} columns selected. The same columns will be included in CSV exports.',
    columnsSelectAll: 'Select all',
    columnsReset: 'Reset to default',
    createExport: 'Create CSV',
    exportHistory: 'Export history',
    exportHistoryHint: 'Files use the current filters and are retained for up to 7 days.',
    exportStatusQUEUED: 'Queued',
    exportStatusRUNNING: 'Generating',
    exportStatusCOMPLETED: 'Ready to download',
    exportStatusFAILED: 'Failed',
    exportStatusEXPIRED: 'Expired',
    exportRows: '{{count}} row',
    exportRows_other: '{{count}} rows',
    exportProviderCount: '{{count}} provider',
    exportProviderCount_other: '{{count}} providers',
    exportAdditionalFilterCount: '{{count}} additional filter',
    exportAdditionalFilterCount_other: '{{count}} additional filters',
    exportExpiresAt: 'Available until {{date}}',
    exportExpiredAt: 'Expired on {{date}}',
    exportShowing: 'Showing {{shown}} of {{total}}',
    exportLoadMore: 'Load more',
    exportLoadingMore: 'Loading more exports',
    exportAllTimeConfirmTitle: 'Create all-time export?',
    exportAllTimeWarning: 'This export includes all time.',
    exportConfirmScope: '{{rows}} · {{providers}}',
    downloadExport: 'Download',
    noExports: 'No exports yet',
    exportCreateError: 'Could not create the export. Please try again.',
    exportHistoryError: 'Could not load export history.',
    exportDownloadError: 'Could not download the export. Please try again.',
    filterDate: 'Date range',
    filterProvider: 'Provider',
    filterArea: 'Area',
    filterGroup: 'Service group',
    filterStatus: 'Trip status',
    filterPayment: 'Payment',
    filterSearch: 'Search',
    filterSearchPlaceholder: 'Search...',
    clearFilters: 'Clear filters',
    allProviders: 'All Providers',
    allAreas: 'All areas',
    allGroups: 'All service groups',
    allStatuses: 'All statuses',
    allPayments: 'All payment states',
    paymentFREE: 'Free trip',
    paymentFINANCE_REVIEW: 'Finance review',
    paymentNOT_BILLABLE: 'Not billable',
    paymentUNBILLED: 'Unbilled',
    paymentOUTSTANDING: 'Outstanding',
    paymentPARTIAL: 'Partially paid',
    paymentSETTLED: 'Settled',
    emptyTitle: 'No data matches these filters',
    emptyBody: 'Try changing the date range or clearing some filters.',
    errorTitle: 'Could not load the report',
    errorBody: 'Please try again.',
    refreshError: 'Could not refresh the report. Showing the last successfully loaded data.',
    deniedTitle: 'You do not have access to this report',
    deniedBody: 'Contact your Provider administrator to request report access.',
    rowsShown: 'Showing {{from}}–{{to}} of {{total}} rows',
    summaryTrips: 'Total Trips',
    summaryFare: 'Fare total',
    summaryBilled: 'Billed',
    summaryReceived: 'Received',
    summaryOutstanding: 'Outstanding',
    summaryReview: 'Needs review',
    tableTitle: 'Trip rows',
    tableHint: 'Scroll horizontally to see every detail column',
    colTripRef: 'Trip ref',
    colServiceDate: 'Service date',
    colStatus: 'Status',
    colPassenger: 'Passenger',
    colAgent: 'Employee',
    colProvider: 'Provider',
    colArea: 'Area',
    colGroup: 'Service group',
    colQuoted: 'Quoted total',
    colFinal: 'Final total',
    colBilled: 'Billed',
    colReceived: 'Received',
    colOutstanding: 'Outstanding',
    colPayment: 'Payment status',
    colReview: 'Review',
    yes: 'Yes',
    no: 'No',
    dictionaryTitle: 'Column dictionary',
    dictionaryIntro: 'UI labels follow the selected language while API field names remain stable.',
    piiLabel: 'Personal data',
    moneyLabel: 'Whole Thai baht',
    dictionarySource: 'Source: {{source}}',
    dictionaryTypeUnit: 'Type: {{type}}{{unit}}',
    dictionaryEmpty: 'Empty value: {{meaning}}',
    dictionaryMeaning: 'Meaning: {{meaning}}',
    dictionarySourceName_trip: 'Trip record',
    dictionarySourceName_passenger: 'Passenger snapshot',
    dictionarySourceName_agent: 'assigned employee snapshot',
    dictionarySourceName_vehicle: 'assigned vehicle snapshot',
    dictionarySourceName_provider: 'Provider snapshot',
    dictionarySourceName_service: 'service configuration snapshot',
    dictionarySourceName_route: 'Trip route and metrics',
    dictionarySourceName_fare: 'canonical fare projection',
    dictionarySourceName_payment: 'canonical payment records',
    dictionarySourceName_derived: 'report projection',
    dictionaryTypeName_identifier: 'identifier',
    dictionaryTypeName_text: 'text',
    dictionaryTypeName_enum: 'fixed state',
    dictionaryTypeName_timestamp: 'ISO-8601 timestamp with Bangkok +07:00 offset',
    dictionaryTypeName_number: 'number',
    dictionaryTypeName_boolean: 'true/false',
    dictionaryTypeName_list: 'list',
    dictionaryUnit_m: ' · metres',
    dictionaryFieldMeaning_locationExceptionPresence:
      'One row per Trip. True when at least one event warns OUTSIDE_RADIUS, LOCATION_UNAVAILABLE or LOW_ACCURACY; false means none. Retained scalar warnings survive geometry purge. Finance is counted once per trip.',
    dictionaryFieldMeaning_locationExceptionCount:
      'Number of qualifying warning events in this trip, not number of trips. Zero means no exception; this computed value is never null. Geometry purge preserves the scalar evidence counted here.',
    dictionaryFieldMeaning_latestLocationException:
      'From the latest qualifying exception, ordered by event time then event ID. Warning/reason filters apply to this same latest exception, not any older event. Retained scalars remain after geometry purge. Null means no exception or this scalar was not recorded; zero is a recorded numeric value. No coordinates or free-text notes are included.',
    dictionaryUnit_none: '',
    dictionaryUnit_thb: ' · whole THB',
    dictionaryUnit_km: ' · km',
    dictionaryUnit_min: ' · minutes',
    dictionaryEmptyMeaning_never: 'not expected; the field is always populated',
    dictionaryEmptyMeaning_notRecorded: 'the event or value has not been recorded',
    dictionaryEmptyMeaning_notAssigned: 'no employee or vehicle was assigned',
    dictionaryEmptyMeaning_notApplicable: 'the concept does not apply to this Trip',
    dictionaryEmptyMeaning_unavailable: 'the source evidence is unavailable; zero must not be inferred',
    dictionaryFieldMeaning_direct: 'the named value from the stated source',
    dictionaryFieldMeaning_requestedAtAlias:
      'currently aliases Trip created_at; it is not a separate persisted request timestamp',
    dictionaryFieldMeaning_tripMilestone:
      'the earliest matching Trip status event; accepted_at uses ASSIGNED or REASSIGNED and is not the current employee acceptance time',
    dictionaryFieldMeaning_agentCodeUnavailable:
      'reserved CSV field; the current API reports it empty even when an employee is assigned',
    dictionaryFieldMeaning_serviceDateBasis:
      'states whether service_date uses scheduled_start or the created_at fallback',
    dictionaryFieldMeaning_pipeSeparated: 'multiple values are joined with | in matching ID/name order',
    dictionaryFieldMeaning_actualMetricSource:
      'records which evidence produced the actual distance or duration',
    dictionaryFieldMeaning_fareState:
      'NONE, PROVISIONAL, FINAL, or CANCELLED_SNAPSHOT; identifies the current fare lifecycle',
    dictionaryFieldMeaning_quoteProvenance:
      'BOOKING_SNAPSHOT when captured at booking; UNAVAILABLE when no quote evidence exists',
    dictionaryFieldMeaning_quotedTotal:
      'booking-time quoted fare; it does not change with later recalculation',
    dictionaryFieldMeaning_currentTotal:
      'current canonical fare used by report summary, whether provisional or final',
    dictionaryFieldMeaning_finalTotal: 'settled final fare only; empty until a final settlement exists',
    dictionaryFieldMeaning_fareAdjustmentDelta:
      'current_total minus quoted_total, including while the fare is provisional',
    dictionaryFieldMeaning_billingMetricSource:
      'records which metric evidence the canonical fare calculation used',
    dictionaryFieldMeaning_paymentComponent:
      'BASE or OVERAGE payment component; empty when that component does not exist',
    dictionaryFieldMeaning_billedTotal: 'sum of canonical billed BASE and OVERAGE amounts for this Trip',
    dictionaryFieldMeaning_completedRefundsTotal:
      'Actual completed cash refunds across BASE, OVERAGE and amendment receipts. Reservations and waivers are not cash refunds.',
    dictionaryFieldMeaning_netRetainedTotal:
      'Gross receipts less completed refunds. Pending refunds remain cash retained until completed.',
    dictionaryFieldMeaning_pendingRefundsTotal:
      'Recorded refund obligations less completed refunds, including the latest Partner refund decision. Pending proposals and fare reductions alone create no refund amount.',
    dictionaryFieldMeaning_reconciliationCashTotal:
      'Unreserved cash allocated to reconciliation. It is separate from recorded pending refunds and is not inferred to be a refund obligation.',
    dictionaryFieldMeaning_receivedTotal:
      'sum of canonical received BASE and OVERAGE amounts, including terminal refund state',
    dictionaryFieldMeaning_outstandingTotal:
      'canonical non-negative billed amount still due after received amounts',
    dictionaryFieldMeaning_paymentState:
      'derived canonical payment state; fully REFUNDED received payments are terminal, not review-only',
    dictionaryFieldMeaning_financeReview:
      'true only when canonical fare/payment evidence requires operator review; fully refunded terminal payments do not',
    dictionaryFieldLabel_trip_id: 'Trip ID',
    dictionaryFieldLabel_trip_ref: 'Trip reference',
    dictionaryFieldLabel_trip_status: 'Trip status',
    dictionaryFieldLabel_trip_leg: 'Trip leg',
    dictionaryFieldLabel_service_mode: 'Service mode',
    dictionaryFieldLabel_dispatch_mode: 'Dispatch mode',
    dictionaryFieldLabel_created_at: 'Created at',
    dictionaryFieldLabel_requested_at: 'Requested at',
    dictionaryFieldLabel_service_date: 'Service date',
    dictionaryFieldLabel_date_basis: 'Service-date basis',
    dictionaryFieldLabel_scheduled_start: 'Scheduled start',
    dictionaryFieldLabel_scheduled_end: 'Scheduled end',
    dictionaryFieldLabel_accepted_at: 'Accepted at',
    dictionaryFieldLabel_started_at: 'Started at',
    dictionaryFieldLabel_picked_up_at: 'Picked up at',
    dictionaryFieldLabel_completed_at: 'Completed at',
    dictionaryFieldLabel_passenger_id: 'Passenger ID',
    dictionaryFieldLabel_passenger_code: 'Passenger code',
    dictionaryFieldLabel_passenger_name: 'Passenger name',
    dictionaryFieldLabel_passenger_phone: 'Passenger phone',
    dictionaryFieldLabel_agent_id: 'Employee ID',
    dictionaryFieldLabel_agent_code: 'Employee code',
    dictionaryFieldLabel_agent_name: 'Employee name',
    dictionaryFieldLabel_agent_phone: 'Employee phone',
    dictionaryFieldLabel_vehicle_id: 'Vehicle ID',
    dictionaryFieldLabel_vehicle_plate: 'Vehicle plate',
    dictionaryFieldLabel_provider_id: 'Provider ID',
    dictionaryFieldLabel_provider_name: 'Provider name',
    dictionaryFieldLabel_service_area_ids: 'Service area IDs',
    dictionaryFieldLabel_service_area_names: 'Service area names',
    dictionaryFieldLabel_service_group_id: 'Service group ID',
    dictionaryFieldLabel_service_group_name: 'Service group name',
    dictionaryFieldLabel_origin_label: 'Origin label',
    dictionaryFieldLabel_destination_label: 'Destination label',
    dictionaryFieldLabel_planned_distance_km: 'Planned distance',
    dictionaryFieldLabel_planned_duration_min: 'Planned duration',
    dictionaryFieldLabel_actual_distance_km: 'Actual distance',
    dictionaryFieldLabel_actual_duration_min: 'Actual duration',
    dictionaryFieldLabel_actual_distance_source: 'Actual-distance source',
    dictionaryFieldLabel_actual_duration_source: 'Actual-duration source',
    dictionaryFieldLabel_currency: 'Fare currency',
    dictionaryFieldLabel_fare_state: 'Fare state',
    dictionaryFieldLabel_quote_provenance: 'Quote provenance',
    dictionaryFieldLabel_quoted_total: 'Quoted total',
    dictionaryFieldLabel_current_total: 'Current fare total',
    dictionaryFieldLabel_final_total: 'Final fare total',
    dictionaryFieldLabel_fare_adjustment_delta: 'Fare adjustment',
    dictionaryFieldLabel_fare_base: 'Base fare',
    dictionaryFieldLabel_fare_overage_distance: 'Distance overage fare',
    dictionaryFieldLabel_fare_overage_time: 'Time overage fare',
    dictionaryFieldLabel_fare_surcharge_total: 'Surcharge total',
    dictionaryFieldLabel_fare_computed_at: 'Fare computed at',
    dictionaryFieldLabel_billing_distance_source: 'Billing-distance source',
    dictionaryFieldLabel_billing_duration_source: 'Billing-duration source',
    dictionaryFieldLabel_billable_distance_km: 'Billable distance',
    dictionaryFieldLabel_billable_duration_min: 'Billable duration',
    dictionaryFieldLabel_fare_is_free: 'Free-fare flag',
    dictionaryFieldLabel_base_payment_status: 'Base payment status',
    dictionaryFieldLabel_base_amount_due: 'Base amount due',
    dictionaryFieldLabel_base_amount_received: 'Base amount received',
    dictionaryFieldLabel_base_outstanding: 'Base outstanding',
    dictionaryFieldLabel_base_due_by: 'Base due by',
    dictionaryFieldLabel_base_verified_at: 'Base verified at',
    dictionaryFieldLabel_overage_payment_status: 'Overage payment status',
    dictionaryFieldLabel_overage_amount_due: 'Overage amount due',
    dictionaryFieldLabel_overage_amount_received: 'Overage amount received',
    dictionaryFieldLabel_overage_outstanding: 'Overage outstanding',
    dictionaryFieldLabel_overage_due_by: 'Overage due by',
    dictionaryFieldLabel_overage_verified_at: 'Overage verified at',
    dictionaryFieldLabel_billed_total: 'Billed total',
    dictionaryFieldLabel_waived_outstanding_total: 'Waived outstanding total',
    dictionaryFieldLabel_overage_review_status: 'Overage review status',
    dictionaryFieldLabel_completed_refunds_total: 'Completed refunds',
    dictionaryFieldLabel_net_retained_total: 'Net cash retained',
    dictionaryFieldLabel_pending_refunds_total: 'Pending refunds',
    dictionaryFieldLabel_reconciliation_cash_total: 'Cash awaiting reconciliation',
    dictionaryFieldLabel_received_total: 'Received total',
    dictionaryFieldLabel_outstanding_total: 'Outstanding total',
    dictionaryFieldLabel_payment_state: 'Payment state',
    confirmation_NOT_REQUIRED: 'Confirmation not required',
    confirmation_OVERRIDE: 'Override accepted',
    confirmation_LEGACY_NO_CONFIRMATION: 'Legacy client without confirmation',
    exceptionExportNotReady:
      'Exception exports are not available yet. Remove exception filters and use default columns to export now. Exception columns and filters require the compatible export service.',
    filterLocationException: 'Location exception',
    allLocationExceptions: 'All trips',
    withLocationException: 'With an exception',
    withoutLocationException: 'Without an exception',
    filterLatestWarning: 'Latest exception warning',
    allLatestWarnings: 'All latest warnings',
    filterLatestOverride: 'Latest exception override reason',
    allLatestOverrides: 'All latest override reasons',
    override_GPS_INACCURATE: 'GPS inaccurate',
    override_MEETING_POINT_INCORRECT: 'Meeting point incorrect',
    override_PASSENGER_REQUESTED_DIFFERENT_POINT: 'Passenger requested a different point',
    override_EMERGENCY: 'Emergency',
    override_OTHER: 'Other',
    dictionaryFieldLabel_has_location_exception: 'Has location exception',
    dictionaryFieldLabel_exception_count: 'Location exception count',
    dictionaryFieldLabel_latest_exception_at: 'Latest exception time',
    dictionaryFieldLabel_latest_exception_event: 'Latest exception event',
    dictionaryFieldLabel_latest_exception_warning: 'Latest exception warning',
    dictionaryFieldLabel_latest_exception_override_reason: 'Latest exception override reason',
    dictionaryFieldLabel_latest_exception_confirmation: 'Latest exception confirmation',
    dictionaryFieldLabel_latest_exception_source: 'Latest exception source',
    dictionaryFieldLabel_latest_exception_accuracy_m: 'Latest exception accuracy (m)',
    dictionaryFieldLabel_latest_exception_distance_to_target_m: 'Latest exception distance to target (m)',
    dictionaryFieldLabel_latest_exception_radius_m: 'Latest exception radius (m)',
    dictionaryFieldLabel_requires_finance_review: 'Requires finance review',
    filterAreaSearch: 'Search area options',
    filterGroupSearch: 'Search service-group options',
    filterOptionSearchPlaceholder: 'Search options',
    filterOptionsLoadMore: 'Load more options',
    filterOptionsError: 'Could not load options',
    selectedOptionLoading: 'Loading selected option {{id}}',
    selectedOptionUnavailable: 'Unavailable selection {{id}}',
    selectedOptionsLoading: 'Resolving selected options…',
    selectedOptionsUnavailable:
      'Some selected options are unavailable in the current Provider scope. Remove them to change the report.',
    registryError: 'Could not load your report access. No report data was requested.',
    unavailableTitle: 'This report is not available yet',
    unavailableBody: 'Choose an available report from the list.',
    retainedLoading: 'Updating filters. Rows and summary below are retained from the previous filters.',
    retainedError:
      'Refresh failed. Rows and summary below still describe the previous filters, not the current filters.',
    retainedBadge: 'Previous filters',
    colCurrentFare: 'Current fare',
    prototypeBadge: 'Review prototype · Local fixture data',
    prototypeNotice:
      'This page uses local fixture data to review layout and interactions. It makes no Report API calls.',
    serviceFrequencyTodayDesc: "Today's job and customer-billing overview",
    dailyServiceFrequencyDesc: 'Daily job trends, statuses, and finance totals',
    cancelledServicesDesc: 'Cancelled jobs, reasons, actors, and finance-review items',
    missedServicesDesc: 'Missed jobs with the latest dispatch evidence',
    driverAssignmentDetailDesc: 'Dispatch V2 Offer, Manual confirmation, and Reassignment history',
    serviceUsersDesc: 'Passengers with a service relationship to the Provider',
    dailyDriverServiceDesc: "Daily work and customer billing attributed to each employee's Trips",
    creatingExport: 'Creating job…',
    providerLogHealth: 'LogHealth',
    providerFoundation: 'เส้นด้าย Foundation',
    areaBangkok: 'Bangkok',
    areaNonthaburi: 'Nonthaburi',
    groupRide: 'Patient transport',
    groupCare: 'Elder care',
    paymentPaid: 'Paid',
    paymentPartial: 'Partially paid',
    paymentOutstanding: 'Outstanding',
    paymentFree: 'Free trip',
    reviewState: 'Review state',
    stateReady: 'Normal data',
    stateLoading: 'Loading',
    stateEmpty: 'Empty',
    stateError: 'Error',
    stateDenied: 'Access denied',
    stateLong: 'Long data / wide table',
    colDate: 'Date',
    colRequests: 'Requests',
    colCompleted: 'Completed',
    colCancelled: 'Cancelled',
    colMissed: 'Missed',
    colReason: 'Reason',
    colOffers: 'Offers',
    colAccepted: 'Accepted',
    colRejected: 'Rejected',
    colName: 'Name',
    colPhone: 'Phone',
    colTrips: 'Trips',
    openTrip: 'Open Trip detail',
    exportTitle: 'CSV export history',
    exportIntro: 'These fixture jobs demonstrate the asynchronous lifecycle and seven-day expiry.',
    exportQueued: 'Queued',
    exportRunning: 'Running',
    exportCompleted: 'Ready to download',
    exportFailed: 'Failed',
    exportExpired: 'Expired',
    download: 'Download',
    expiresIn: 'Expires in 7 days',
    rowCount: '{{count}} rows',
    customerBillingNote: 'Finance values are customer billing attributed to Trips, not employee payout.',
  },
};
